import { newPieceIdFor, newPieceRecord } from '@/domain/newPieceFlow';
import { selfBookLinkByToken, selfBookLinkKey, useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { useSessionStore } from '@/state/sessionStore';
import { useUIStore } from '@/state/uiStore';
import { isStoreFallbackEnabled } from '@/api/apiMode';
import { payDepositIntent, createAndPayIntent, holdSlotIntent, intentFailureCopy, placeDepositIntent, reinstateDepositIntent, resendLinkIntent, sendSelfBookLinkIntent, supersedeLinkIntent, type IntentFailure, type NewPiecePayload } from '@/api/intents';
import {
  advanceLeadStage,
  isLinkSweepable,
  isLiveBooking,
  slotBookingId,
  onFileExpiryFrom,
  overlapsFirm,
  payBookingEffect,
  planProtectsLink,
  resendDecision,
  type NewDateReason,
  type PayBookingEffect,
  type ResendVia,
} from '@atink/domain';
import { authHeaders } from '@/api/persist';
import { withServerAuthoredWrites } from '@/state/persistenceBridge';
import { flushWritesAndWait } from '@/state/writeQueue';
import { playSound } from '@/sound/audioSystem';
import { eventBus } from '@/events/eventBus';
import { autoPingChannelFor } from '@/utils/commsHelpers';
import { onBookingConfirmed } from '@/domain/bookingConfirmationFlow';
import { holdExpiresAtIso, holdExpiryHours, isThisWeek } from '@/domain/depositWindow';
import { depositForSession, sessionLengthForDuration } from '@/domain/pricing';
import { recallOverlappingHolds } from '@/domain/resendRoutingFlow';
import { bookingBriefLine, depositPaidAud } from '@/utils/bookingSummary';
import { resolveNoDepositDecline } from '@/domain/noDepositDeclineFlow';
import { seedNow, seedId } from '@/domain/seedContext';
import { currentActorId } from '@/domain/actor';
import { customerDepositUrl } from '@/utils/customerLinks';
import { deliverDepositMessage, depositMessageRoute } from '@/domain/depositMessageDelivery';
import { depositResendMessage, type DepositLinkRoute } from '@/domain/depositLinkSend';
import { windowNowIso } from '@/domain/messengerWindow';
import type {
  Booking,
  CustomerRecord,
  Deposit,
  DepositChannel,
  DepositLedgerEntry,
  LinkSenderRole,
  SelfBookLink,
  SessionLength,
  StudioId,
  TopupPlan,
} from '@/domain/types';

const STRIPE_FEE_RATE = 0.0175;
const STRIPE_FIXED_FEE = 0.3;

/** Reserved identity for a slot held by a link before a client has claimed it. */
export const AWAITING_CLIENT = 'awaiting-client';

/**
 * Deposit ids with a server-backed pay-deposit intent currently in flight
 * (tv-rp7.1). The held→paid flip now lands ASYNC (in the intent's `.then`), so
 * the top-of-function `deposit.status !== 'pending'` guard no longer closes the
 * window: a second webhook for the SAME deposit fired within the round-trip would
 * pass it, fire a second intent, and double-run the side-effects (stats / events
 * double-counted). This Set closes that window synchronously — added before the
 * intent call, removed when it settles.
 */
const inFlightPayDeposits = new Set<string>();

/**
 * Deposit ids with a server-backed create-and-pay intent currently in flight
 * (tv-rp7.2). Same window as inFlightPayDeposits: the fresh booking+deposit create
 * now lands ASYNC (in the intent's `.then`), so a second submit for the SAME
 * deposit within the round-trip must NOT fire a second intent nor double-run the
 * side-effects. Added before the intent call, removed when it settles.
 */
const inFlightCreateAndPay = new Set<string>();

/**
 * Hold-slot intents built in the CURRENT synchronous turn but not yet sent
 * (tv-2na.7), keyed by deposit id. DepositLinkModal.onSend calls
 * generateDepositLink and then createTopupPlan in the same turn; the plan's own
 * write-through PUT used to race the in-flight hold and fail the deposit FK
 * (5 retries, then the plan was lost). generateDepositLink now sends the intent
 * on a microtask, so a plan created for that deposit in the same turn JOINS the
 * intent (attachTopupPlanToPendingHold) and lands in the same transaction.
 */
const pendingHoldSlots = new Map<string, { topupPlan?: TopupPlan }>();

// The optimistic ghost is visible before its hold transaction commits. Any
// simulated payment must wait for that transaction, not for a fixed UI timer.
const inFlightHoldSlots = new Map<string, Promise<boolean>>();

/**
 * If a hold-slot intent for `plan.depositId` is still being assembled (same
 * turn), mirror the plan into the store WITHOUT a bridge PUT and ship it inside
 * that intent. Returns false when there is no such pending hold — the caller
 * then writes the plan the ordinary way.
 */
export function attachTopupPlanToPendingHold(plan: TopupPlan): boolean {
  const pending = pendingHoldSlots.get(plan.depositId);
  if (!pending) return false;
  pending.topupPlan = plan;
  withServerAuthoredWrites([['topupPlans', plan.id]], () => {
    useBrainStore.getState().upsertTopupPlan(plan);
  });
  return true;
}

export interface DepositLinkInput {
  /** The client confirmed on the call (a Sweeper gap-fill): the held booking skips the cadence (7kv1). */
  confirmedOnCall?: boolean;
  /**
   * deposits-10: this link replaces an expired one ("Pick a new date" on a
   * resend). Once the fresh hold lands, the old link is closed against it.
   */
  resendOfDepositId?: string;
  /**
   * Caller-provided deposit id. Used by the SERVER-BACKED atomic self-book path
   * (completeUniversalSelfBook), which must know the id UP FRONT to suppress this
   * create's write (withServerAuthoredWrites) before routing the confirm through
   * the create-and-pay intent — otherwise the coalesced pending-deposit PUT would
   * race the intent (tv-rp7.2). Left unset by every other caller (deterministic
   * seedId / timestamp id as before).
   */
  id?: string;
  customerId: string;
  studioId: StudioId;
  artistId: string;
  amountAud: number;
  channel: 'sms' | 'meta-dm' | 'ig-dm' | 'email';
  sessionLength: Booking['sessionLength'];
  source: Booking['source'];
  bookingStartIso?: string;
  designBrief?: string;
  referencePhotos?: string[];
  /**
   * Slot discussed + pinned on the call. When present, `discussedSlot.artistId`
   * becomes the deposit's artist (instead of `input.artistId`) so the later
   * booking lands with the right artist.
   */
  discussedSlot?: { artistId: string; startIso: string; endIso: string };
  /** Human-readable piece name captured on the call. */
  pieceName?: string;
  /** Reconciliation code from the self-book link this deposit was taken through. */
  depositReference?: string;
  /**
   * Structured brief captured on the previous screen. When a slot is grounded,
   * these ride onto the held ghost booking so it opens pre-filled in the booking
   * editor (add reference images etc.) rather than as a bare hold.
   */
  placement?: string;
  bodyPart?: Booking['bodyPart'];
  bodyPartSide?: Booking['bodyPartSide'];
  bodyPartQualifier?: Booking['bodyPartQualifier'];
  styleTags?: string[];
  colour?: 'colour' | 'black-and-grey';
  collabArtistIds?: string[];
  /** Existing piece this session continues (from the brief's Continue mode). */
  pieceId?: string;
  /** A NEW multi-session piece (tv-2na.11.22): created server-side in the hold-slot intent. */
  newPiece?: NewPiecePayload;
  /** Session day rate quoted at send time (built from the artist rate x window). */
  dayRateAud?: number;
  /** How the day rate was set (New booking's custom rate), onto the held booking. */
  dayRateBasis?: Booking['dayRateBasis'];
  /** The booking's one-line summary and the desk's notes, onto the held booking
   *  (they used to ride only on the removed demo auto-pay, tv-k9b.5). */
  summary?: string;
  notes?: string;
  /** B7 (8xat D08-09): "Happy to come in earlier if a slot opens", onto the held booking. */
  preferBroughtForward?: boolean;
  /**
   * Firm hold: opt out of the this-week +2h grace and the overbookable flag, so
   * the hold is a flat 24h lock with no overbook (tv-nid). Set by the Sweeper
   * gap-fill dated-link path; agent dated-links leave it unset (overbookable when
   * this week).
   */
  firmHold?: boolean;
  /**
   * In-person pay-at-studio hold: no link is sent. Suppresses the
   * `deposit-link-sent` activity, stamps the deposit `payAtStudio`, and pins the
   * hold's `expiresAt` to the appointment window (so the link clock never kills
   * it — `reconcileDepositHolds` exempts it outright).
   */
  holdOnly?: boolean;
  /**
   * A BRAND-NEW customer not yet on file (e.g. a walk-in captured at the desk,
   * tv-2na.8.2). Server-backed with a grounded slot it is created INSIDE the
   * hold-slot intent (no racing customer PUT ahead of the pair); otherwise it is
   * simply upserted first. Must be `customerId`.
   */
  customer?: CustomerRecord;
  /**
   * W5 (DECIDED 24 Sep): who is sending this link, when the caller knows better
   * than the session (the Sweeper gap-fill passes 'sweeper'). Otherwise derived:
   * an artist / self-book link is customer-driven, else the session persona.
   */
  sentByRole?: LinkSenderRole;
  /**
   * The DEPOSIT's own source when it differs from the held booking's (the
   * Sweeper gap-fill link is `source: 'gap-fill'`; its booking keeps a real
   * booking source). Defaults to `source`.
   */
  depositSource?: Deposit['source'];
  /**
   * L1 (14ym9bva056): the server texts the client the pay link inside the
   * hold-slot intent (the Sweeper gap-fill, which has no other sender). Served
   * builds only; the caller writes its own line in store fallback.
   */
  textLink?: boolean;
}

/**
 * Who sent a link (W5): explicit, else customer-driven for an artist / self-book
 * link, else from the session persona (the Messenger inbox vs every staff desk,
 * which is an Agent path). Undefined with no session (seed / replay) — the
 * missedLinks fallback reads the source then.
 */
function resolveLinkSender(input: DepositLinkInput): LinkSenderRole | undefined {
  if (input.sentByRole) return input.sentByRole;
  const ctxRole = workContextSender(input.customerId);
  if (ctxRole) return ctxRole;
  if (input.source === 'artist-link' || input.source === 'self-book-link' || input.customerId === AWAITING_CLIENT) {
    return 'self-book';
  }
  const persona = useSessionStore.getState().persona;
  if (!persona) return undefined;
  return persona === 'messenger' ? 'messenger' : 'agent';
}

/**
 * The sender a live work context implies for a link to THIS customer (W5b):
 * a "Pick a new date" resend carries its resender (Q3). Undefined otherwise.
 */
function workContextSender(customerId: string): LinkSenderRole | undefined {
  const ctx = useUIStore.getState().workContext;
  if (!ctx || ctx.customerId !== customerId) return undefined;
  if (ctx.kind === 'resend') return ctx.sentByRole;
  if (ctx.kind === 'gap-fill') return 'sweeper';
  return undefined;
}

/**
 * Inside the Sweeper's gap window (W5b #3, D19) every link to that customer is a
 * Sweeper gap-fill link, whichever path sent it: source 'gap-fill' on the
 * deposit and a firm 24h hold (no this-week overbook). Explicit input wins.
 */
function applyWorkContext(input: DepositLinkInput): DepositLinkInput {
  const ctx = useUIStore.getState().workContext;
  if (ctx?.kind !== 'gap-fill' || ctx.customerId !== input.customerId) return input;
  return {
    ...input,
    depositSource: input.depositSource ?? 'gap-fill',
    firmHold: input.firmHold ?? true,
  };
}

/**
 * Who is RE-sending a link (Q3, DECIDED 25 Sep: whoever resent it owns the
 * follow-up): the surface's explicit role, else the work context, else the
 * session persona. Unlike a first send, the old link's source (an artist /
 * self-book link) never decides it — a person pressed Resend.
 */
function resolveResender(customerId: string, explicit?: LinkSenderRole): LinkSenderRole | undefined {
  if (explicit) return explicit;
  const ctxRole = workContextSender(customerId);
  if (ctxRole) return ctxRole;
  const persona = useSessionStore.getState().persona;
  if (!persona) return undefined;
  return persona === 'messenger' ? 'messenger' : 'agent';
}

function nowIso() {
  return seedNow() ?? useSimulationStore.getState().clockIso;
}

/** The current session operator, for per-agent deposit attribution (Prompt F). */
function currentAgentId(): string | undefined {
  return useSessionStore.getState().agentId ?? undefined;
}

/**
 * B3 (Anas, 30 Sep): when Ops declines a no-deposit request, the $0 hold keeps its
 * slot on a normal pay-by. This is that pending link: the artist's standard deposit
 * for the session, on the usual hold window, cross-referenced to the held booking.
 * Paid, it confirms the booking (and lifts the decline); unpaid, the link sweep
 * cancels the hold. Built here so it matches every other link's shape and fees.
 */
export function declinedHoldDepositLink(booking: Booking): Deposit {
  const now = nowIso();
  const artist = useBrainStore.getState().artists[booking.artistId];
  // No rate card on file: the studio's standard deposit for the session.
  const amountAud = depositForSession(booking.sessionLength, artist?.rates ? artist : undefined);
  const id = `dep-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
  const endIso = new Date(new Date(booking.start).getTime() + booking.durationMin * 60_000).toISOString();
  return {
    id,
    customerId: booking.customerId,
    studioId: booking.studioId,
    artistId: booking.artistId,
    amountAud,
    feesAud: Number((amountAud * STRIPE_FEE_RATE + STRIPE_FIXED_FEE).toFixed(2)),
    netAud: Number((amountAud - amountAud * STRIPE_FEE_RATE - STRIPE_FIXED_FEE).toFixed(2)),
    status: 'pending',
    channelSent: 'sms',
    stripePaymentLinkId: `plink_mock_${id}`,
    expiresAt: holdExpiresAtIso(booking.start, now),
    createdAt: now,
    policy: 'seven-day-rule',
    source: booking.source,
    discussedSlot: { artistId: booking.artistId, startIso: booking.start, endIso },
    heldBookingId: booking.id,
  };
}

/**
 * deposits-10: close the old link against the fresh one (server first), then
 * mirror it: expired, superseded, resent, its held ghost released. A refusal
 * leaves the old row as it was and says so.
 */
async function supersedeOldLink(oldDepositId: string, freshDepositId: string): Promise<void> {
  const res = await supersedeLinkIntent(oldDepositId, freshDepositId);
  if (!res.ok) {
    useUIStore.getState().pushNotification({
      intensity: 'info',
      title: 'Old link still open',
      body: "The new link went out, but the old one couldn't be closed. It may still show Resend.",
      ttlMs: 6000,
    });
    return;
  }
  const b = useBrainStore.getState();
  const old = b.deposits[oldDepositId];
  const ghostId = res.releasedBookingId;
  withServerAuthoredWrites(
    [['deposits', oldDepositId], ...(ghostId ? ([['bookings', ghostId]] as const) : [])],
    () => {
      if (old) b.upsertDeposit({ ...old, status: 'expired', supersededByDepositId: freshDepositId, resentAt: nowIso() });
      const ghost = ghostId ? b.bookings[ghostId] : undefined;
      if (ghost) b.upsertBooking({ ...ghost, status: 'cancelled' });
    },
  );
}

/**
 * The free-text brief the held booking carries (tv-2mwa): the piece name the
 * agent typed, else the brief's description, else the structured brief as one line ("Fine line
 * · black & grey · Inner forearm"), and only with none of those the
 * "Held on deposit link." placeholder screens treat as no brief.
 */
function holdBrief(input: DepositLinkInput): string {
  if (input.pieceName) return input.pieceName;
  // A pay-at-studio hold keeps the placeholder, so screens can say "Held to pay
  // at the studio" (holdBriefText); its structured brief still rides the booking.
  if (input.holdOnly) return 'Held on deposit link.';
  if (input.designBrief?.trim()) return input.designBrief.trim();
  const line = bookingBriefLine({
    designBrief: '',
    ...(input.styleTags ? { styleTags: input.styleTags } : {}),
    ...(input.colour ? { colour: input.colour } : {}),
    ...(input.bodyPart ? { bodyPart: input.bodyPart } : {}),
    ...(input.bodyPartSide ? { bodyPartSide: input.bodyPartSide } : {}),
    ...(input.bodyPartQualifier ? { bodyPartQualifier: input.bodyPartQualifier } : {}),
    ...(input.placement ? { placement: input.placement } : {}),
  });
  return line || 'Held on deposit link.';
}

export function generateDepositLink(input: DepositLinkInput): Deposit {
  input = applyWorkContext(input); // W5b #3: a Sweeper gap window's link is a gap-fill link
  const id = input.id ?? seedId('dep') ?? `dep-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
  const now = nowIso();
  const slot = input.discussedSlot;
  // A pay-at-studio hold isn't on the link clock: pin its window to the slot end
  // (the client settles at the desk when they arrive), so the flat link expiry
  // can't kill it. reconcileDepositHolds also exempts it outright (the real guard).
  const expiresAt = slot
    ? input.holdOnly
      ? slot.endIso
      : holdExpiresAtIso(slot.startIso, now, { firm: input.firmHold })
    : new Date(new Date(now).getTime() + 48 * 60 * 60 * 1000).toISOString();
  const heldBookingId = slot ? `bk-${id}` : undefined;
  const sentByRole = resolveLinkSender(input);
  const sentBy = currentActorId();
  // A this-week agent dated-link holds an OVERBOOKABLE ghost: the near slot may be
  // double-held and locks to whoever pays first (tv-nid). Firm (gap-fill) and
  // out-of-week holds are never overbookable.
  // A pay-at-studio hold holds no money and is never over-bookable (8tqu, Anas 1 Oct).
  const overbookable = !!slot && !input.firmHold && !input.holdOnly && isThisWeek(slot.startIso, now);
  // The booking + deposit belong at the ARTIST's studio (where the tattoo
  // happens), NOT the customer's primary/preferred studio. A cross-studio pick
  // (customer at studio A, artist at studio B) otherwise files the ghost under a
  // studio the artist isn't in, so it shows on NEITHER calendar (tv-k9b.3). Fall
  // back to the caller's studio only when the artist is unknown.
  const effectiveArtistId = slot?.artistId ?? input.artistId;
  const bookingStudioId =
    (effectiveArtistId
      ? useBrainStore.getState().artists[effectiveArtistId]?.studioId
      : undefined) ?? input.studioId;
  const deposit: Deposit = {
    id,
    customerId: input.customerId,
    studioId: bookingStudioId,
    artistId: slot?.artistId ?? input.artistId,
    amountAud: input.amountAud,
    feesAud: Number((input.amountAud * STRIPE_FEE_RATE + STRIPE_FIXED_FEE).toFixed(2)),
    netAud: Number(
      (input.amountAud - input.amountAud * STRIPE_FEE_RATE - STRIPE_FIXED_FEE).toFixed(2),
    ),
    status: 'pending',
    channelSent: input.channel,
    stripePaymentLinkId: `plink_mock_${id}`,
    expiresAt,
    createdAt: now,
    policy: 'seven-day-rule',
    source: input.depositSource ?? input.source,
    ...(currentAgentId() ? { takenByAgentId: currentAgentId() } : {}),
    ...(sentByRole ? { sentByRole } : {}),
    ...(sentByRole && sentBy ? { sentBy } : {}),
    ...(slot ? { discussedSlot: slot } : {}),
    ...(heldBookingId ? { heldBookingId } : {}),
    ...(input.pieceName ? { pieceName: input.pieceName } : {}),
    ...(input.depositReference ? { depositReference: input.depositReference } : {}),
    ...(input.holdOnly ? { payAtStudio: true } : {}),
    // 75mu: the deposit belongs to the piece from its first session.
    ...(input.newPiece ? { pieceId: newPieceIdFor(id) } : input.pieceId ? { pieceId: input.pieceId } : {}),
  };
  const brain = useBrainStore.getState();

  // The held ghost booking, built up front (when a slot is grounded) so the SAME
  // record feeds both the store mirror and the atomic intent payload.
  const held: Booking | undefined =
    slot && heldBookingId
      ? {
          id: heldBookingId,
          customerId: input.customerId,
          studioId: bookingStudioId,
          artistId: slot.artistId,
          start: slot.startIso,
          durationMin: Math.max(
            15,
            Math.round(
              (new Date(slot.endIso).getTime() - new Date(slot.startIso).getTime()) / 60000,
            ),
          ),
          sessionLength: input.sessionLength,
          status: 'held',
          depositId: id,
          ...(overbookable ? { overbookable: true } : {}),
          // A Sweeper gap-fill is confirmed on the call: the stamp rides the hold
          // itself (7kv1), never a follow-up write that can race the intent.
          ...(input.confirmedOnCall ? { confirmedOnCall: true } : {}),
          ...(input.preferBroughtForward ? { preferBroughtForward: true } : {}),
          designBrief: holdBrief(input),
          referencePhotos: input.referencePhotos ?? [],
          source: input.source as Booking['source'],
          createdAt: now,
          // Carry the structured brief so the ghost booking is editable in place
          // (the booking editor reads these), not a bare hold.
          ...(input.placement ? { placement: input.placement } : {}),
          ...(input.bodyPart ? { bodyPart: input.bodyPart } : {}),
          ...(input.bodyPartSide ? { bodyPartSide: input.bodyPartSide } : {}),
          ...(input.bodyPartQualifier ? { bodyPartQualifier: input.bodyPartQualifier } : {}),
          ...(input.styleTags && input.styleTags.length ? { styleTags: input.styleTags } : {}),
          ...(input.colour ? { colour: input.colour } : {}),
          ...(input.collabArtistIds && input.collabArtistIds.length
            ? { collabArtistIds: input.collabArtistIds }
            : {}),
          ...(input.newPiece ? { pieceId: newPieceIdFor(id) } : input.pieceId ? { pieceId: input.pieceId } : {}),
          ...(input.dayRateAud != null ? { dayRateAud: input.dayRateAud } : {}),
          ...(input.dayRateBasis ? { dayRateBasis: input.dayRateBasis } : {}),
          ...(input.summary ? { summary: input.summary } : {}),
          ...(input.notes ? { notes: input.notes } : {}),
        }
      : undefined;

  const heldPiece = held && input.newPiece ? newPieceRecord(input.newPiece, id, held) : undefined;

  // SERVER-BACKED grounded-slot hold (tv-3ps.7): the held booking + pending deposit
  // are a cross-referencing pair (booking.depositId <-> deposit.heldBookingId). The
  // old flow persisted them as two independent write-through PUTs which race and, on
  // a flaky API, DROP one — leaving the customer's pay page reading "expired or not
  // generated" with no booking on the calendar. Route the pair through the ONE-
  // transaction hold-slot intent (both land or neither does), mirroring it into the
  // store WITHOUT the bridge PUTs (withServerAuthoredWrites) so the calendar shows
  // the ghost immediately; a failed intent removes the pair (nothing was persisted
  // server-side) and surfaces a staff toast.
  if (held && heldBookingId && !isStoreFallbackEnabled()) {
    const newCustomer = input.customer;
    withServerAuthoredWrites(
      [
        ...(newCustomer ? ([['customers', newCustomer.id]] as const) : []),
        ...(heldPiece ? ([['pieces', heldPiece.id]] as const) : []),
        ['deposits', id],
        ['bookings', heldBookingId],
      ],
      () => {
        if (newCustomer) brain.upsertCustomer(newCustomer);
        if (heldPiece) brain.upsertPiece(heldPiece);
        brain.upsertDeposit(deposit);
        brain.upsertBooking(held);
      },
    );

    const pending: { topupPlan?: TopupPlan } = {};
    pendingHoldSlots.set(id, pending);

    const revertHold = () => {
      const planId = pending.topupPlan?.id;
      withServerAuthoredWrites(
        [
          ...(newCustomer ? ([['customers', newCustomer.id]] as const) : []),
        ...(heldPiece ? ([['pieces', heldPiece.id]] as const) : []),
          ['deposits', id],
          ['bookings', heldBookingId],
          ...(planId ? ([['topupPlans', planId]] as const) : []),
        ],
        () => {
          useBrainStore.setState((s) => {
            const deposits = { ...s.deposits };
            delete deposits[id];
            const bookings = { ...s.bookings };
            delete bookings[heldBookingId];
            const customers = newCustomer ? { ...s.customers } : s.customers;
            if (newCustomer) delete customers[newCustomer.id];
            const pieces = heldPiece ? { ...s.pieces } : s.pieces;
            if (heldPiece) delete pieces[heldPiece.id];
            if (!planId) return { deposits, bookings, customers, pieces };
            const topupPlans = { ...s.topupPlans };
            delete topupPlans[planId];
            return { deposits, bookings, customers, pieces, topupPlans };
          });
        },
      );
      useUIStore.getState().pushNotification({
        intensity: 'critical',
        title: "Couldn't hold the slot",
        body: "The deposit link didn't send, so the slot wasn't held. Please try again.",
        ttlMs: 10000,
      });
    };

    // Sent on a microtask so a top-up plan created for this deposit in the same
    // turn rides inside the intent (tv-2na.7) instead of a racing PUT.
    const holdReady = Promise.resolve()
      .then(() => {
        pendingHoldSlots.delete(id);
        return holdSlotIntent({
          bookingId: heldBookingId,
          depositId: id,
          booking: held,
          deposit,
          ...(newCustomer ? { customer: newCustomer } : {}),
          ...(pending.topupPlan ? { topupPlan: pending.topupPlan } : {}),
          ...(input.newPiece ? { newPiece: input.newPiece } : {}),
          ...(input.textLink ? { textLink: true } : {}),
        });
      })
      .then(async (res) => {
        // An idempotent replay (alreadyExists) is still a success — the pair is
        // durable — so only a genuine refusal reverts.
        if (!res.ok) revertHold();
        else if (input.resendOfDepositId) await supersedeOldLink(input.resendOfDepositId, id);
        return res.ok;
      })
      .catch((err) => {
        console.error('[hold-slot] send failed', err);
        revertHold();
        return false;
      })
      .finally(() => inFlightHoldSlots.delete(id));
    inFlightHoldSlots.set(id, holdReady);
  } else {
    // STORE-FALLBACK (unit tests / standalone) OR a dateless link (no held booking):
    // keep the client write-through verbatim — the bridge PUTs each record.
    if (input.customer) brain.upsertCustomer(input.customer);
    if (heldPiece) brain.upsertPiece(heldPiece);
    brain.upsertDeposit(deposit);
    if (held) brain.upsertBooking(held);
  }

  if (held) {
    brain.appendActivity({
      id: `act-${id}-hold`,
      type: 'booking-created',
      customerId: input.customerId,
      studioId: input.studioId,
      artistId: slot!.artistId,
      body: `Held ${slot!.artistId}'s slot ${new Date(slot!.startIso).toLocaleString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })} pending deposit.`,
      timestamp: now,
    });
  }

  // A pay-at-studio hold sends NO link, so it must NOT log a "deposit link sent"
  // activity — that would be a lie in the live feed. The truthful "Held … pending
  // deposit." activity above is the single hold activity for this path.
  if (!input.holdOnly) {
    const hours = slot ? holdExpiryHours(slot.startIso, now) : 48;
    brain.appendActivity({
      id: `act-${id}`,
      type: 'deposit-link-sent',
      customerId: input.customerId,
      studioId: input.studioId,
      artistId: input.artistId,
      amount: input.amountAud,
      body: input.customerId === AWAITING_CLIENT
        ? `Self-book slot held via artist link. ${formatAud(input.amountAud)}, expires in ${hours}h.`
        : `Stripe deposit link sent via ${input.channel} to ${input.customerId}. ${formatAud(input.amountAud)}, expires in ${hours}h.`,
      timestamp: now,
    });
  }
  return deposit;
}

function formatAud(n: number) {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
  }).format(n);
}

/**
 * A successful deposit payment issues a receipt to the customer (Diagram 02:
 * "the system collects the phone number and sends the receipt") and confirms the
 * phone is a real SMS channel on file. Prototype-only: no real SMS, just the
 * activity record + the number-on-file stamp. Module-private, so it adds no new
 * export (console coverage unaffected). Fires once per payment instant.
 */
function emitDepositReceipt(deposit: Deposit, amountAud: number, at: string): void {
  const brain = useBrainStore.getState();
  const customer = brain.customers[deposit.customerId];
  if (customer && !customer.numberOnFile) {
    brain.upsertCustomer({ ...customer, numberOnFile: true });
  }
  brain.appendActivity({
    id: `act-receipt-${deposit.id}-${at}`,
    type: 'receipt-sent',
    customerId: deposit.customerId,
    studioId: deposit.studioId,
    artistId: deposit.artistId,
    amount: amountAud,
    body: `Receipt for ${formatAud(amountAud)} deposit sent to ${deposit.customerId}. Number on file.`,
    timestamp: at,
  });
}

export function simulateDepositLinkViewed(depositId: string) {
  const brain = useBrainStore.getState();
  const deposit = brain.deposits[depositId];
  if (!deposit) return;
  if (deposit.status !== 'pending' || deposit.viewedAt) return;
  brain.upsertDeposit({ ...deposit, viewedAt: nowIso() });
  brain.appendActivity({
    id: `act-dep-view-${deposit.id}`,
    type: 'deposit-link-sent',
    customerId: deposit.customerId,
    studioId: deposit.studioId,
    body: `${deposit.customerId} opened the deposit link.`,
    timestamp: nowIso(),
  });
}

export function simulateStripeWebhookPaid(opts: {
  depositId: string;
  bookingStartIso?: string;
  designBrief?: string;
  referencePhotos?: string[];
  /** Structured brief fields captured at booking time, merged onto the created booking. */
  bookingOverrides?: Partial<Booking>;
  /**
   * SERVER-BACKED atomic create (self-book open path): route the fresh booking +
   * paid deposit through the create-and-pay intent (ONE deferred tx) instead of two
   * client writes, so the circular booking.depositId <-> deposit.bookingId FK never
   * deadlocks the per-id write-through bridge (tv-3ps.6). Only honoured in the
   * slot-known branch when store-fallback is OFF; the held branch owns its own
   * pay-deposit intent, and the Stripe-link path (sendDepositLink) leaves this
   * unset so it keeps the existing two-write flow unchanged.
   */
  atomicCreate?: boolean;
  /**
   * A prerequisite awaited BEFORE the atomic create-and-pay POST fires (server-
   * backed atomicCreate only). Self-book mints a brand-new customer in the SAME
   * tick, and bookings/deposits carry a NOT-NULL FK to customers — the old two-PUT
   * path survived on the write-queue's FK-miss retries, but the single-POST intent
   * has none, so the customer MUST be committed first. completeUniversalSelfBook
   * passes a persist-the-new-customer promise here; if it rejects, the create is
   * abandoned (nothing charged/committed) and the failure is surfaced.
   */
  beforeAtomicIntent?: () => Promise<unknown>;
  /**
   * Demo / seed only: a deposit minted in this same tick with no slot is paid
   * straight onto file (the "deposit paid, awaiting placement" story). Without
   * it, a pending link with no date is never paid (7g3y: a link always carries a
   * date; the server refuses it too).
   */
  newOnFileWithoutDate?: boolean;
  /**
   * D05-11: the email the simulated customer typed. The server takes no money
   * through a staff token without an email on the customer; this fills an empty one.
   */
  contactEmail?: string;
}): void {
  const holdReady = inFlightHoldSlots.get(opts.depositId);
  if (holdReady) {
    void holdReady.then((held) => {
      // Re-enter only after success, re-reading the deposit and its status.
      // A rejected hold has already removed its optimistic records and alerted
      // staff. It must never trigger a payment or confirmation side-effects.
      if (held) simulateStripeWebhookPaid(opts);
    });
    return;
  }
  const brain = useBrainStore.getState();
  const deposit = brain.deposits[opts.depositId];
  if (!deposit) return;
  // Idempotency / replay guard: only a still-pending deposit is actionable. A late
  // or replayed webhook on one already paid/expired/cancelled must be a no-op - an
  // expired hold's slot has been released, so re-confirming it would resurrect a
  // cancelled booking, double-count revenue, and text the client a false "locked
  // in" (reconcileDepositHolds leaves heldBookingId in place, so the branch below
  // would otherwise re-enter).
  if (deposit.status !== 'pending') return;

  // tv-b26: an actual deposit landing clears any no-deposit decline flag on this
  // customer (covers all three confirm branches below with one call). Served build:
  // the server already treats a deposit paid after the decline as lifting it (B3),
  // and only Ops may write the flag, so there is nothing to send.
  if (isStoreFallbackEnabled()) resolveNoDepositDecline(deposit.customerId);

  // Linked-booking path: the deposit already has its booking — a placeholder held
  // on send (heldBookingId) OR a booking linked by bookingId (e.g. part-paid,
  // `pending-deposit`). Confirm THAT booking in place (no fresh booking), promote
  // heldBookingId -> bookingId, and lock it. payTargetBooking is the one rule for
  // which booking a pay firms, shared with the server's runPayDeposit (tv-2na.8.14).
  {
    const target = payTargetBooking(deposit, brain.bookings);
    if (target) {
      const { booking: held, effect } = target;
      const firms = effect === 'firm';
      // Paying makes the booking SOLID (status confirmed → is_firm true, blocks the
      // slot), but the confirmation CADENCE (confirmationFlow.ts) owns confirmedAt —
      // do NOT stamp it on pay (tv-xue). A paid held booking is solid yet still awaits
      // its call, so it stays in the confirmation worklist until a human confirms.
      const at = nowIso();
      // A checked-in / in-chair booking is firmed in place (the server's 'firm-in-place').
      const confirmed: Booking = !firms ? { ...held, overbookable: false } : {
        ...held,
        status: 'confirmed',
        // Match what the server intent writes (held ghost → firm): the mirrored
        // optimistic value must not keep the ghost's overbookable:true, or the
        // client-side is_firm derivation would diverge from the DB (tv-rp7.1).
        overbookable: false,
        ...opts.bookingOverrides,
      };
      const { heldBookingId: _promoted, ...rest } = deposit;
      // Only what is still owed lands (the server's pay-deposit rule), so money
      // already on the deposit is never charged twice.
      const owedNow = owedOnDeposit(deposit);
      const paid: Deposit = {
        ...rest,
        status: 'paid',
        amountPaid: deposit.amountAud,
        paidAt: at,
        bookingId: confirmed.id,
        ledger: [
          ...(deposit.ledger ?? []),
          { at, amountAud: owedNow, kind: 'paid', by: 'stripe', note: 'Stripe link' },
        ],
      };

      // The side-effects that ride EVERY confirm path — stat bumps, the
      // deposit-paid activity, the sequence clear, the receipt, sound, the event,
      // the confirmation-flow kickoff and the overbook recall. These are separate
      // entities / UX (not the booking+deposit the intent owns), so they persist
      // through the NORMAL bridge path in both modes.
      const runConfirmSideEffects = () => {
        mirrorLeadStageBooked(deposit.customerId);
        brain.incStat('deposits');
        brain.incStat('networkRevenueAud', owedNow);
        brain.appendActivity({
          id: `act-${confirmed.id}-confirmed`,
          type: 'deposit-paid',
          customerId: deposit.customerId,
          studioId: deposit.studioId,
          artistId: confirmed.artistId,
          amount: owedNow,
          body: firms
            ? `Deposit ${formatAud(owedNow)} paid by ${deposit.customerId}. ${held.status === 'held' ? 'Held slot' : 'Booking'} confirmed.`
            : `Deposit ${formatAud(owedNow)} paid by ${deposit.customerId}. Deposit settled.`,
          timestamp: at,
        });
        brain.clearSequenceState(deposit.customerId);
        emitDepositReceipt(deposit, owedNow, at);
        playSound('deposit-chime');
        eventBus.publish('stripe-webhook-success', { bookingId: confirmed.id, depositId: deposit.id });
        // The client is already here on a 'leave' pay: no confirmation / waiver
        // send and no hold recall (the booking was firm before the pay).
        if (firms) {
          onBookingConfirmed(confirmed.id, { paidOnServer: true });
          // Someone paid first: void + re-call any other overbookable hold on this slot.
          recallOverlappingHolds(confirmed.id);
        }
      };

      // Mirror the server's committed flip into the store WITHOUT a second PUT
      // (withServerAuthoredWrites) so the mounted view patches optimistically
      // (ghost→solid). Idempotent to re-run (same values), but only ever run for
      // a genuine confirmation below.
      const applyConfirm = () => {
        withServerAuthoredWrites(
          [
            ['bookings', confirmed.id],
            ['deposits', deposit.id],
          ],
          () => {
            brain.upsertBooking(confirmed);
            brain.upsertDeposit(paid);
          },
        );
      };

      // Surface an intent failure to BOTH audiences. The customer pay route
      // renders outside AppShell, so NotificationStack (and any toast) is absent
      // there — a toast alone would strand the customer on HostedPaymentMock's
      // optimistic "booking locked in" success with the deposit still pending. So
      // publish a failure event the pay page/mock reset on, AND keep the staff
      // toast for the agent-triggered (in-shell) case. The deposit is left pending
      // server-side, which is correct — nothing was charged/committed.
      const onPayFailed = (message?: string) => {
        eventBus.publish('stripe-webhook-failed', { depositId: deposit.id });
        useUIStore.getState().pushNotification({
          intensity: 'critical',
          title: "Couldn't confirm the deposit",
          body:
            message ??
            "The payment didn't confirm, so nothing was charged. Please try again.",
          ttlMs: 10000,
        });
      };

      // SERVER-BACKED (the served build / E2E): the ATOMIC pay-deposit intent
      // owns the booking+deposit flip in ONE transaction (tv-rp7.1), so there is
      // no client-computed double PUT that a reconnecting viewer can catch mid-tear.
      if (!isStoreFallbackEnabled()) {
        // Close the concurrency window synchronously (see inFlightPayDeposits): a
        // second webhook for this deposit within the round-trip must NOT fire a
        // second intent nor double-run the side-effects.
        if (inFlightPayDeposits.has(deposit.id)) return;
        inFlightPayDeposits.add(deposit.id);
        void (opts.contactEmail ? payDepositIntent(deposit.id, { contactEmail: opts.contactEmail }) : payDepositIntent(deposit.id))
          .then((res) => {
            if (res.ok) {
              mirrorCapturedEmail(deposit.customerId, opts.contactEmail);
              applyConfirm();
              // An idempotent replay (alreadyPaid) means the FIRST confirmation
              // already ran the side-effects (stats / events / receipt) — mirror
              // the record but do NOT re-run them (double-count guard).
              if (!res.alreadyPaid) runConfirmSideEffects();
            } else {
              onPayFailed(res.message);
            }
          })
          .catch((err) => {
            // The .then body (store mirror / side-effects) can throw; never leave
            // an unhandled rejection or strand the customer on a false success.
            console.error('[pay-deposit] confirm failed', err);
            onPayFailed();
          })
          .finally(() => {
            inFlightPayDeposits.delete(deposit.id);
          });
        return;
      }

      // STORE-FALLBACK (unit tests / standalone): no API server, so keep the
      // existing client-side flip verbatim — the persistence bridge PUTs both.
      brain.upsertBooking(confirmed);
      brain.upsertDeposit(paid);
      runConfirmSideEffects();
      return;
    }
  }

  // Effective slot: an explicit bookingStartIso wins, else fall back to the slot
  // grounded onto the deposit when the link was sent (the gap-fill / discussed-slot
  // path). This makes a grounded deposit land in its slot the moment it's paid.
  const groundedStartIso = opts.bookingStartIso ?? deposit.discussedSlot?.startIso;

  // No slot at all (canonical cold path: agent sends deposit, customer pays,
  // THEN the queue surfaces a placement task and the agent picks the slot).
  // Mark the deposit paid, fire the activity, leave the booking for the agent
  // to place. The deposit-paid event is what drives the queue's deposit-on-file
  // item via queueEngine.
  // Only what is still owed lands (the server's pay-deposit rule), so money
  // already on a part-paid deposit is never charged twice (tv-2na.8.14).
  const owedNow = owedOnDeposit(deposit);

  if (!groundedStartIso) {
    if (!opts.newOnFileWithoutDate && owedNow >= deposit.amountAud) {
      // A deposit link always carries a date (diagram 03; Anas 1 Oct, 7g3y): an
      // older dateless link with nothing paid takes no money, so nothing goes on
      // file (the server's rule too). Money already in may take its balance.
      eventBus.publish('stripe-webhook-failed', { depositId: deposit.id });
      useUIStore.getState().pushNotification({
        intensity: 'high',
        title: 'This link needs a date',
        body: "It has no date, so it can't be paid. Send a new link with a date.",
        ttlMs: 6000,
      });
      return;
    }
    const paidAt = nowIso();
    brain.upsertDeposit({
      ...deposit,
      status: 'paid',
      amountPaid: deposit.amountAud,
      paidAt,
      // Dateless on file (the "abyss", Diagram 02): valid 12 months from the
      // instant it went on file, so the Sweeper chases it in the on-file cohort.
      expiresAt: onFileExpiryFrom(paidAt),
      ledger: [...(deposit.ledger ?? []), { at: nowIso(), amountAud: owedNow, kind: 'paid', by: 'stripe', note: 'Stripe link' }],
    });
    brain.incStat('deposits');
    brain.incStat('networkRevenueAud', owedNow);
    brain.appendActivity({
      id: `act-dep-paid-${deposit.id}`,
      type: 'deposit-paid',
      customerId: deposit.customerId,
      studioId: deposit.studioId,
      artistId: deposit.artistId,
      amount: owedNow,
      body: `Deposit ${formatAud(owedNow)} paid by ${deposit.customerId}. Place their booking now.`,
      timestamp: nowIso(),
    });
    const target = autoPingChannelFor('deposit-paid', deposit.studioId, brain.internalComms);
    if (target) {
      brain.addAutoPing(
        target,
        `💰 ${formatAud(owedNow)} deposit landed · ${deposit.studioId.replace('-', ' ')} · awaiting placement.`,
        { customerRefId: deposit.customerId, timestamp: nowIso() },
      );
    }
    emitDepositReceipt(deposit, owedNow, paidAt);
    playSound('deposit-chime');
    eventBus.publish('stripe-webhook-success', { depositId: deposit.id });
    return;
  }

  // Slot already known: canonical scripted scenarios + self-book flow pre-pick
  // the time so we can confirm the booking the moment the webhook fires.
  const bookingId = `bk-${deposit.id}`;
  const startIso = groundedStartIso;
  const sessionLength: Booking['sessionLength'] =
    deposit.amountAud >= 500 ? 'full-day' : deposit.amountAud >= 300 ? 'half-day' : 'small';
  const durationMin = sessionLength === 'full-day' ? 480 : sessionLength === 'half-day' ? 240 : 120;

  const booking: Booking = {
    id: bookingId,
    customerId: deposit.customerId,
    studioId: deposit.studioId,
    artistId: deposit.artistId ?? 'pat',
    start: startIso,
    durationMin,
    sessionLength,
    status: 'confirmed',
    depositId: deposit.id,
    designBrief: opts.designBrief ?? 'Confirmed booking via simulated Stripe webhook.',
    referencePhotos: opts.referencePhotos ?? [],
    source: deposit.source as Booking['source'],
    createdAt: nowIso(),
    ...opts.bookingOverrides,
  };

  const paidDeposit: Deposit = {
    ...deposit,
    status: 'paid',
    amountPaid: deposit.amountAud,
    paidAt: nowIso(),
    bookingId,
    ledger: [...(deposit.ledger ?? []), { at: nowIso(), amountAud: owedNow, kind: 'paid', by: 'stripe', note: 'Stripe link' }],
  };

  // The store mirror of the confirmed pair (deposit paid + fresh booking). In the
  // atomic path the server already persisted these, so it runs inside
  // withServerAuthoredWrites (bridge skips the PUTs, still patches the cache).
  const applyConfirm = (bk: Booking) => {
    brain.upsertDeposit(paidDeposit);
    brain.upsertBooking(bk);
  };

  // The side-effects that ride EVERY confirm on this path (stats, activities,
  // receipt, sound, event, waiver kickoff, overbook recall) — separate entities /
  // UX, so they persist through the NORMAL bridge path in both modes.
  // `paidOnServer`: only the atomic create-and-pay ran a server pay (and texted the
  // receipt); the store-fallback / non-atomic two-PUT path still confirms here.
  const runConfirmSideEffects = (paidOnServer: boolean) => {
    mirrorLeadStageBooked(deposit.customerId);
    brain.incStat('deposits');
    brain.incStat('networkRevenueAud', owedNow);
    brain.appendActivity({
      id: `act-${bookingId}`,
      type: 'deposit-paid',
      customerId: deposit.customerId,
      studioId: deposit.studioId,
      artistId: booking.artistId,
      amount: owedNow,
      body: `Deposit ${formatAud(owedNow)} paid by ${deposit.customerId}. Booking confirmed.`,
      timestamp: nowIso(),
    });
    brain.appendActivity({
      id: `act-${bookingId}-booking`,
      type: 'booking-created',
      customerId: deposit.customerId,
      studioId: deposit.studioId,
      artistId: booking.artistId,
      body: `${deposit.customerId} x ${booking.artistId}. ${new Date(startIso).toLocaleString('en-AU', { weekday: 'long', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}. ${sessionLength}.`,
      timestamp: nowIso(),
    });
    emitDepositReceipt(deposit, owedNow, nowIso());
    playSound('deposit-chime');
    eventBus.publish('stripe-webhook-success', { bookingId, depositId: deposit.id });
    // Booking just reached `confirmed` - auto-send the pre-session waiver.
    onBookingConfirmed(bookingId, { paidOnServer });
    // Someone paid first: void + re-call any other overbookable hold on this slot.
    recallOverlappingHolds(bookingId);
  };

  // SERVER-BACKED atomic create-and-pay (self-book open path): the fresh booking +
  // paid deposit go through ONE deferred transaction — no client double-write to
  // deadlock on the circular FK (tv-3ps.6). The flip lands only after the intent
  // confirms; a concurrent replay never double-fires; an idempotent alreadyPaid
  // mirrors the record without re-running side-effects; a failed intent applies
  // nothing and surfaces the failure to BOTH the customer flow and staff.
  if (opts.atomicCreate && !isStoreFallbackEnabled()) {
    if (inFlightCreateAndPay.has(deposit.id)) return;
    inFlightCreateAndPay.add(deposit.id);

    // SOLID: confirmed + not over-bookable (is_firm true) — mirror exactly what the
    // server writes, so the client is_firm derivation matches the DB until reload.
    const confirmed: Booking = { ...booking, overbookable: false };

    const onPayFailed = (message?: string) => {
      // The customer pay route renders outside AppShell (no toast there): publish
      // the failure event the pay page resets on, AND keep the staff toast for the
      // in-shell case. The deposit is left pending — nothing was charged/committed.
      eventBus.publish('stripe-webhook-failed', { depositId: deposit.id });
      useUIStore.getState().pushNotification({
        intensity: 'critical',
        title: "Couldn't confirm the booking",
        body:
          message ?? "The payment didn't confirm, so nothing was charged. Please try again.",
        ttlMs: 10000,
      });
    };

    const runIntent = () =>
      createAndPayIntent({
        bookingId,
        depositId: deposit.id,
        booking: confirmed,
        deposit, // the pending deposit; the server owns the flip to paid
        payment: { amountAud: deposit.amountAud, by: 'stripe', note: 'Stripe link' },
        ...(opts.contactEmail ? { contactEmail: opts.contactEmail } : {}),
      })
        .then((res) => {
          if (res.ok) {
            withServerAuthoredWrites(
              [
                ['bookings', bookingId],
                ['deposits', deposit.id],
              ],
              () => applyConfirm(confirmed),
            );
            if (!res.alreadyPaid) runConfirmSideEffects(true);
          } else {
            onPayFailed(res.message);
          }
        })
        .catch((err) => {
          console.error('[create-and-pay] self-book failed', err);
          onPayFailed();
        })
        .finally(() => {
          inFlightCreateAndPay.delete(deposit.id);
        });

    // With a prerequisite (self-book's new-customer commit), await it FIRST, then
    // fire the intent; a rejected prerequisite abandons the create (nothing
    // committed) and surfaces the failure. Without one, fire synchronously so the
    // intent is in flight the instant the branch runs.
    if (opts.beforeAtomicIntent) {
      void Promise.resolve()
        .then(() => opts.beforeAtomicIntent!())
        .then(runIntent)
        .catch((err) => {
          console.error('[create-and-pay] self-book prerequisite failed', err);
          onPayFailed();
          inFlightCreateAndPay.delete(deposit.id);
        });
    } else {
      void runIntent();
    }
    return;
  }

  // STORE-FALLBACK (unit tests / standalone) OR the non-atomic Stripe-link path:
  // keep the client-side two writes verbatim — the persistence bridge PUTs both.
  applyConfirm(booking);
  runConfirmSideEffects(false);
}

/**
 * A fresh booking id for a placement (B4, 8tqt). Never `bk-<depositId>`: that id
 * belongs to the deposit's first hold, which an Ops cancel / no-date release /
 * no-show keeps as history — re-deriving it 422'd (cancelled -> confirmed) or,
 * for a no-show, silently overwrote the dead row.
 */
export function newPlacementBookingId(depositId: string): string {
  return seedId('bk-pl') ?? `bk-pl-${Date.now()}-${Math.floor(Math.random() * 1e6).toString(36)}-${depositId.slice(-4)}`;
}

export function placeBookingFromDeposit(opts: {
  depositId: string;
  artistId: string;
  startIso: string;
  durationMin?: number;
  designBrief?: string;
  /**
   * Who is placing it (B4): 'sweeper' = a gap placement on the call, confirmed
   * on the call so it skips the cadence (diagram 08); 'agent' (default) keeps the
   * normal cadence.
   */
  via?: 'sweeper' | 'agent';
}): Promise<{ ok: boolean; bookingId?: string }> | undefined {
  const brain = useBrainStore.getState();
  const deposit = brain.deposits[opts.depositId];
  if (!deposit) return undefined;
  if (deposit.status !== 'paid') return undefined;
  if (deposit.bookingId) return undefined; // already placed

  const sessionLength: Booking['sessionLength'] =
    deposit.amountAud >= 500 ? 'full-day' : deposit.amountAud >= 300 ? 'half-day' : 'small';
  const durationMin =
    opts.durationMin ??
    (sessionLength === 'full-day' ? 480 : sessionLength === 'half-day' ? 240 : 120);
  const via = opts.via ?? 'agent';

  const bookingId = newPlacementBookingId(deposit.id);
  const booking: Booking = {
    id: bookingId,
    customerId: deposit.customerId,
    // The artist's studio, not the deposit's (B7): a cross-studio placement reads
    // as booked on the artist's calendar, and the server refuses a mismatch.
    studioId: brain.artists[opts.artistId]?.studioId ?? deposit.studioId,
    artistId: opts.artistId,
    start: opts.startIso,
    durationMin,
    sessionLength,
    status: 'confirmed',
    depositId: deposit.id,
    designBrief:
      opts.designBrief ?? deposit.pieceName ?? 'Booking placed after deposit paid.',
    referencePhotos: [],
    source: deposit.source as Booking['source'],
    createdAt: nowIso(),
    ...(via === 'sweeper' ? { confirmedOnCall: true } : {}),
    ...(deposit.pieceId ? { pieceId: deposit.pieceId } : {}),
    ...(currentAgentId() ? { bookedByAgentId: currentAgentId() } : {}),
  };

  // SERVER-BACKED (W5b D25): ONE place-deposit intent creates the booking and
  // links the deposit, or neither. The old two PUTs sent the deposit's bookingId
  // before its booking existed (a live 500, then a retry). The store mirrors the
  // pair — and the side-effects run — only once the server says ok.
  if (!isStoreFallbackEnabled()) {
    return placeDepositIntent({ depositId: deposit.id, bookingId, booking, via }).then((res) => {
      if (!res.ok) {
        const copy = intentFailureCopy(res);
        useUIStore.getState().pushNotification({
          intensity: 'critical',
          title: copy.title,
          body: copy.retry
            ? "We couldn't reach the server, so the deposit wasn't placed. Try again."
            : `The deposit wasn't placed: ${(res.message ?? res.error).replace(/\.+$/, '')}.`,
          ttlMs: 8000,
          customerId: deposit.customerId,
        });
        return { ok: false };
      }
      // An idempotent replay answers the booking that already holds it.
      const placedId = res.bookingId ?? bookingId;
      const placed = placedId === bookingId ? booking : { ...booking, id: placedId };
      withServerAuthoredWrites([['deposits', deposit.id], ['bookings', placedId]], () => {
        const b = useBrainStore.getState();
        b.upsertDeposit({ ...(b.deposits[deposit.id] ?? deposit), bookingId: placedId, artistId: opts.artistId });
        if (!res.alreadyPlaced || !b.bookings[placedId]) b.upsertBooking(placed);
      });
      if (!res.alreadyPlaced) afterDepositPlaced(deposit, placed, opts.artistId, opts.startIso, { textedOnServer: true });
      return { ok: true, bookingId: placedId };
    });
  }

  brain.upsertDeposit({ ...deposit, bookingId, artistId: opts.artistId });
  brain.upsertBooking(booking);
  afterDepositPlaced(deposit, booking, opts.artistId, opts.startIso);
  return Promise.resolve({ ok: true, bookingId });
}

/**
 * The side-effects of a landed placement (activity, sequence, waiver, recall).
 * `textedOnServer` (L1, tv-vbpk): the place-deposit intent already texted the
 * confirmation and the waiver link, so only the calendar invite is added here.
 */
function afterDepositPlaced(
  deposit: Deposit,
  booking: Booking,
  artistId: string,
  startIso: string,
  opts: { textedOnServer?: boolean } = {},
): void {
  const brain = useBrainStore.getState();
  const bookingId = booking.id;
  // The deposit's earlier booking (cancelled / released / no-show) stays as history; name it.
  const prior = Object.values(brain.bookings).find(
    (b) => b.depositId === deposit.id && b.id !== bookingId && !isLiveBooking(b),
  );
  brain.appendActivity({
    id: `act-${bookingId}-placed`,
    type: 'booking-created',
    customerId: deposit.customerId,
    studioId: deposit.studioId,
    artistId,
    body: `Placed ${deposit.customerId} with ${artistId} on ${new Date(startIso).toLocaleString('en-AU', { weekday: 'long', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}${
      prior ? ` (deposit carried from ${prior.status} booking ${prior.id})` : ''
    }.`,
    timestamp: nowIso(),
  });
  brain.clearSequenceState(deposit.customerId);
  eventBus.publish('booking:placed', { bookingId, depositId: deposit.id });
  // Booking just reached `confirmed`: the waiver + confirmation (the server's, when served).
  onBookingConfirmed(bookingId, { textedOnServer: opts.textedOnServer });
  // Slot now locked: void + re-call any other overbookable hold on this slot.
  recallOverlappingHolds(bookingId);
}

const MANUAL_METHOD_LABEL: Record<'eftpos' | 'cash' | 'bank-transfer', string> = {
  eftpos: 'EFTPOS',
  cash: 'Cash',
  'bank-transfer': 'Bank transfer',
};

/**
 * Record a deposit payment taken by a non-Stripe method (EFTPOS / cash / bank
 * transfer). Appends a ledger entry and bumps the deposit's paid amount - the
 * money row's "deposit paid" and balance derive from this, so nothing is
 * hand-typed. Every successful payment issues a receipt (Diagram 02).
 *
 * Two shapes:
 * - `bookingId`: pay against a dated booking. Not paid for yet -> ONE atomic
 *   intent via payBookingDepositAtDesk (the booking ends firm, the deposit linked —
 *   tv-2na.8.11); an already-paid deposit just gains the movement.
 * - `customerId` + `studioId` with no (resolvable) booking: record a floating,
 *   on-file paid deposit valid 12 months (the "abyss" the Sweeper chases) - the
 *   agent took the money before a date exists.
 */
export function recordManualPayment(opts: {
  bookingId?: string;
  /**
   * The fresh booking record for the SERVER-BACKED atomic create (desk
   * take-payment-now): when supplied and store-fallback is OFF, the booking is
   * NOT yet in the store — it is created together with the minted paid deposit
   * through the ONE-transaction create-and-pay intent, so the circular
   * booking.depositId <-> deposit.bookingId FK never deadlocks the per-id
   * write-through bridge (tv-3ps.6). Ignored in store-fallback (the caller upserts
   * the booking first and the two-write path below runs verbatim).
   */
  booking?: Booking;
  newPiece?: NewPiecePayload;
  customerId?: string;
  studioId?: StudioId;
  amountAud: number;
  method: 'eftpos' | 'cash' | 'bank-transfer';
  /** Where the money settled (card/transfer/cash); rides onto the ledger entry. */
  channel?: DepositChannel;
  by?: string;
  /** D05-11: the client's email from the take-payment form (fills an empty one on file). */
  contactEmail?: string;
  /**
   * Booking-level side-effects the CALLER owns (its booking-created activity,
   * waiver kickoff, confirm toast). Server-backed, they run only AFTER the atomic
   * intent commits (and never on an idempotent replay); store-fallback, the caller
   * runs them itself.
   */
  onServerConfirmed?: () => void;
  /**
   * Called (server-backed atomic path only) when the intent is refused / errors,
   * so the caller can re-enable its double-submit-latched take-deposit action for a
   * retry. Nothing was charged or committed.
   */
  onServerFailed?: () => void;
}): void {
  if (opts.amountAud <= 0) return;
  const brain = useBrainStore.getState();
  const at = nowIso();
  const note = MANUAL_METHOD_LABEL[opts.method];
  const entry: DepositLedgerEntry = {
    at,
    amountAud: opts.amountAud,
    kind: 'paid',
    by: opts.by,
    note,
    ...(opts.channel ? { channel: opts.channel } : {}),
  };

  // SERVER-BACKED atomic create-and-pay: create the fresh booking AND its paid
  // deposit in ONE deferred transaction (see the `booking` param). The two client
  // writes this replaces (booking + deposit) coalesce per-id and collapse the
  // FK-safe intermediate, deadlocking on the circular FK (tv-3ps.6).
  if (opts.booking && !isStoreFallbackEnabled()) {
    payManualViaCreateAndPay(opts, opts.booking);
    return;
  }

  const booking = opts.bookingId ? brain.bookings[opts.bookingId] : undefined;

  // No booking to attach to: a floating, on-file paid deposit that enters the
  // 12-month on-file cohort. Needs an owner (customer + studio) to be a record.
  if (!booking) {
    const { customerId, studioId } = opts;
    if (!customerId || !studioId) return;
    const id = `dep-manual-${Date.now()}`;
    const deposit: Deposit = {
      id,
      customerId,
      studioId,
      amountAud: opts.amountAud,
      amountPaid: opts.amountAud,
      feesAud: 0,
      netAud: opts.amountAud,
      status: 'paid',
      channelSent: 'walk-in',
      stripePaymentLinkId: 'manual',
      method: opts.method,
      // On file, no date: valid 12 months from when it landed.
      expiresAt: onFileExpiryFrom(at),
      createdAt: at,
      paidAt: at,
      policy: 'seven-day-rule',
      source: 'agent-initiated-chat',
      ledger: [entry],
      ...(currentAgentId() ? { takenByAgentId: currentAgentId() } : {}),
    };
    brain.upsertDeposit(deposit);
    brain.incStat('deposits');
    brain.appendActivity({
      id: `act-manualpay-onfile-${id}`,
      type: 'deposit-paid',
      customerId,
      studioId,
      amount: opts.amountAud,
      body: `Manual payment ${formatAud(opts.amountAud)} (${note}) taken on file for ${customerId}. Awaiting a date.`,
      timestamp: at,
    });
    emitDepositReceipt(deposit, opts.amountAud, at);
    return;
  }

  // A booking whose deposit is already PAID (e.g. a part-paid plan topped up at
  // the desk): one deposit write — the additive ledger merge keeps every movement.
  const existing = booking.depositId ? brain.deposits[booking.depositId] : undefined;
  if (existing && existing.status === 'paid') {
    const prevPaid = existing.amountPaid ?? existing.amountAud;
    const updated: Deposit = {
      ...existing,
      amountPaid: prevPaid + opts.amountAud,
      method: opts.method,
      paidAt: existing.paidAt ?? at,
      ledger: [...(existing.ledger ?? []), entry],
    };
    brain.upsertDeposit(updated);
    brain.appendActivity({
      id: `act-manualpay-${booking.id}-${Date.now()}`,
      type: 'deposit-paid',
      customerId: booking.customerId,
      studioId: booking.studioId,
      artistId: booking.artistId,
      amount: opts.amountAud,
      body: `Manual payment ${formatAud(opts.amountAud)} (${note}) recorded on ${booking.customerId}'s booking.`,
      timestamp: at,
    });
    emitDepositReceipt(updated, opts.amountAud, at);
    return;
  }

  // Otherwise the booking is not paid for yet: take the deposit through ONE atomic
  // intent (tv-2na.8.11) — never the old two-PUT pair (upsertDeposit +
  // upsertBooking) that left the booking held and the deposit without a booking_id.
  void payBookingDepositAtDesk({
    bookingId: booking.id,
    amountAud: opts.amountAud,
    method: opts.method,
    ...(opts.channel ? { channel: opts.channel } : {}),
    ...(opts.by ? { by: opts.by } : {}),
    ...(opts.contactEmail ? { contactEmail: opts.contactEmail } : {}),
    onFailed: (f) => {
      const copy = intentFailureCopy(f);
      useUIStore.getState().pushNotification({ intensity: 'critical', title: copy.title, body: copy.body, ttlMs: 10000 });
    },
  });
}

/** Ledger channel for a desk method (where the money settled). */
const METHOD_CHANNEL: Record<'eftpos' | 'cash' | 'bank-transfer', DepositChannel> = {
  eftpos: 'card',
  cash: 'cash',
  'bank-transfer': 'transfer',
};

/**
 * How a desk-taken deposit reached us: the Agent's seat takes it over the PHONE
 * ('voice' — card over phone, board 05), anyone else in the shop ('walk-in').
 * A phone payment must never be recorded as a walk-in (tv-2na.8.11).
 */
function deskChannelSent(method: 'eftpos' | 'cash' | 'bank-transfer'): Deposit['channelSent'] {
  if (method !== 'cash' && useSessionStore.getState().persona === 'bookings-agent') return 'voice';
  return 'walk-in';
}

export interface DeskBookingPaymentInput {
  /** The EXISTING booking (already in the store) the deposit is taken for. */
  bookingId: string;
  amountAud: number;
  method: 'eftpos' | 'cash' | 'bank-transfer';
  /** Where the money settled; defaults from the method (EFTPOS = card). */
  channel?: DepositChannel;
  /** Who took it; defaults to the signed-in person (currentActorId). */
  by?: string;
  /** D05-11: the client's email from the form; the server needs one on file to take money. */
  contactEmail?: string;
  /** Runs once, after the server commits (store-fallback: synchronously). */
  onConfirmed?: (r: { bookingId: string; depositId: string; amountAud: number }) => void;
  onFailed?: (f: IntentFailure) => void;
}

export type DeskBookingPaymentResult =
  | { ok: true; bookingId: string; depositId: string; amountAud: number }
  | IntentFailure;

/**
 * Take a deposit at the desk / over the phone for an EXISTING booking (Edit
 * booking > Take deposit, the console's manual payment) in ONE server
 * transaction (tv-2na.8.11):
 *  - the booking already holds a PENDING link deposit -> the pay-deposit intent
 *    pays THAT deposit (held -> confirmed + firm, deposit paid + linked), with the
 *    person, method and channel on the ledger — so the link can't later be paid
 *    twice or expire over a paid booking;
 *  - no live deposit yet -> create-and-pay mints the paid deposit and firms the
 *    booking together (the circular booking <-> deposit FK in one deferred tx).
 * Any edits still queued for the booking are flushed and answered BEFORE the
 * intent, so its transaction reads them and their PUT can't race in behind it.
 * Nothing is written to the store until the server answers; then the committed
 * end state is mirrored without bridge PUTs, and the confirm side-effects run
 * (receipt, waiver + confirmation, recall of other holds on the slot).
 */
export function payBookingDepositAtDesk(input: DeskBookingPaymentInput): Promise<DeskBookingPaymentResult> {
  const brain = useBrainStore.getState();
  const booking = brain.bookings[input.bookingId];
  const fail = (f: IntentFailure): DeskBookingPaymentResult => {
    input.onFailed?.(f);
    return f;
  };
  if (!booking) return Promise.resolve(fail({ ok: false, kind: 'rejected', status: 0, error: 'no-booking' }));

  const by = input.by ?? currentActorId() ?? 'front-desk';
  const channel = input.channel ?? METHOD_CHANNEL[input.method];
  const note = MANUAL_METHOD_LABEL[input.method];
  const linked = booking.depositId ? brain.deposits[booking.depositId] : undefined;
  const pending =
    linked?.status === 'pending'
      ? linked
      : Object.values(brain.deposits).find((d) => d.status === 'pending' && slotBookingId(d) === booking.id);

  // The ONE pay rule (payBookingEffect): a checked-in / in-chair booking settles
  // its deposit but is LEFT as it is — no downgrade to confirmed, and no
  // confirmation / waiver send or hold recall for a client who is already here.
  const firms = payBookingEffect(booking.status) === 'firm';
  const afterConfirmed = (depositId: string, amountAud: number) => {
    if (firms) {
      onBookingConfirmed(booking.id, { paidOnServer: true });
      recallOverlappingHolds(booking.id);
    }
    input.onConfirmed?.({ bookingId: booking.id, depositId, amountAud });
    return { ok: true as const, bookingId: booking.id, depositId, amountAud };
  };

  // ── No live deposit: create + pay it with the booking in ONE create-and-pay.
  if (!pending) {
    const createAndPay = () =>
      createAndPayAtDesk({
        booking: useBrainStore.getState().bookings[booking.id] ?? booking,
        amountAud: input.amountAud,
        method: input.method,
        channel,
        channelSent: deskChannelSent(input.method),
        by,
        ...(input.contactEmail ? { contactEmail: input.contactEmail } : {}),
      }).then((r) => (r.ok ? afterConfirmed(r.depositId, input.amountAud) : fail(r)));
    // Store fallback lands synchronously (createAndPayAtDesk confirms before its
    // promise settles); served, the queued edit is answered first.
    return isStoreFallbackEnabled() ? createAndPay() : flushWritesAndWait().then(createAndPay);
  }

  // ── A pending link deposit: pay THAT one through the pay-deposit intent.
  const payment = { by, note, channel, method: input.method };
  const mirror = () => {
    // FIRST, before any side-effect writes the customer (the receipt's
    // numberOnFile), so that write carries the server's 'booked'.
    mirrorLeadStageBooked(booking.customerId);
    const b = useBrainStore.getState();
    const at = nowIso();
    const current = b.bookings[booking.id] ?? booking;
    const dep = b.deposits[pending.id] ?? pending;
    const owedNow = owedOnDeposit(dep);
    const paid: Deposit = {
      ...dep,
      status: 'paid',
      amountPaid: dep.amountAud,
      paidAt: at,
      bookingId: booking.id,
      heldBookingId: undefined,
      method: input.method,
      ledger: [...(dep.ledger ?? []), { at, amountAud: owedNow, kind: 'paid', by, note, channel }],
    };
    const confirmed: Booking =
      payBookingEffect(current.status) === 'firm'
        ? { ...current, status: 'confirmed', overbookable: false, depositId: pending.id }
        : { ...current, overbookable: false, depositId: pending.id };
    const apply = () => {
      b.upsertDeposit(paid);
      b.upsertBooking(confirmed);
    };
    if (isStoreFallbackEnabled()) apply();
    else withServerAuthoredWrites([['deposits', pending.id], ['bookings', booking.id]], apply);
    b.incStat('deposits');
    b.appendActivity({
      id: `act-manualpay-${booking.id}-${pending.id}`,
      type: 'deposit-paid',
      customerId: booking.customerId,
      studioId: booking.studioId,
      artistId: booking.artistId,
      amount: owedNow,
      body: `Manual payment ${formatAud(owedNow)} (${note}) recorded on ${booking.customerId}'s booking.`,
      timestamp: at,
    });
    emitDepositReceipt(paid, owedNow, at);
    mirrorCapturedEmail(booking.customerId, input.contactEmail);
    return afterConfirmed(pending.id, owedNow);
  };

  if (isStoreFallbackEnabled()) return Promise.resolve(mirror());
  return flushWritesAndWait()
    .then(() => payDepositIntent(pending.id, { payment, ...(input.contactEmail ? { contactEmail: input.contactEmail } : {}) }))
    .then((res): DeskBookingPaymentResult => (res.ok ? mirror() : fail(res)))
    .catch((err): DeskBookingPaymentResult => {
      console.error('[pay-deposit] desk payment failed', err);
      return fail({ ok: false, kind: 'transient', status: 0, error: 'network' });
    });
}

/**
 * Server-backed desk take-payment-now from NewBookingModal (recordManualPayment
 * with `booking`): a thin adapter over the ONE desk helper, createAndPayAtDesk.
 * Keeps its callback contract (onServerConfirmed / onServerFailed) and toasts the
 * failure with the shared intentFailureCopy.
 */
function payManualViaCreateAndPay(
  opts: {
    amountAud: number;
    newPiece?: NewPiecePayload;
    method: ManualMethod;
    channel?: DepositChannel;
    by?: string;
    contactEmail?: string;
    onServerConfirmed?: () => void;
    onServerFailed?: () => void;
  },
  booking: Booking,
): void {
  void createAndPayAtDesk({
    booking,
    ...(opts.newPiece ? { newPiece: opts.newPiece } : {}),
    amountAud: opts.amountAud,
    method: opts.method,
    ...(opts.channel ? { channel: opts.channel } : {}),
    ...(opts.by ? { by: opts.by } : {}),
    ...(opts.contactEmail ? { contactEmail: opts.contactEmail } : {}),
    onConfirmed: () => opts.onServerConfirmed?.(),
    onFailed: (f) => {
      const copy = intentFailureCopy(f);
      useUIStore.getState().pushNotification({ intensity: 'critical', title: copy.title, body: copy.body, ttlMs: 10000 });
      // Let the caller re-enable its (latched) take-deposit action so staff can retry.
      opts.onServerFailed?.();
    },
  });
}

type ManualMethod = 'eftpos' | 'cash' | 'bank-transfer';

export interface DeskCreateAndPayInput {
  /** The FRESH booking (not yet in the store or the DB). Its lifecycle end state
   *  is the server's: confirmed, firm, cross-referenced, no confirmedAt. */
  booking: Booking;
  /** Re-send the SAME deposit id to retry after a transient failure (idempotent).
   *  Omitted: a fresh `dep-manual-` id is minted. */
  depositId?: string;
  amountAud: number;
  method: ManualMethod;
  /** Where the money settled (card / cash / transfer); rides onto the ledger. */
  channel?: DepositChannel;
  /** How the client reached us (deposit.channelSent); default 'walk-in' (the desk). */
  channelSent?: Deposit['channelSent'];
  by?: string;
  /** A BRAND-NEW customer (not yet on file) — created in the SAME transaction.
   *  Must be booking.customerId. Never upsert it before calling this. */
  customer?: CustomerRecord;
  /** A NEW multi-session piece (tv-2na.11.22): created in the SAME create-and-pay. */
  newPiece?: NewPiecePayload;
  /** D05-11: the client's email from the take-payment form. The server takes no
   *  staff payment without an email on file; this fills an empty one. */
  contactEmail?: string;
  /** The caller's side-effects (activity, waiver + confirmation SMS, success
   *  toast, closing the modal). Run exactly once, only AFTER the server commits
   *  (store-fallback: synchronously, before the returned promise settles). */
  onConfirmed?: (r: { bookingId: string; depositId: string }) => void;
  /** The server refused / never answered. Nothing was mirrored. */
  onFailed?: (f: DeskCreateAndPayFailure) => void;
}

export type DeskCreateAndPayFailure = IntentFailure & { bookingId: string; depositId: string };
export type DeskCreateAndPayResult =
  | { ok: true; bookingId: string; depositId: string; alreadyExists: boolean }
  | DeskCreateAndPayFailure;

/** Deposit ids whose create-and-pay failed TRANSIENTLY on this client: the first
 *  POST may have committed with its response lost, so an `alreadyExists` on the
 *  retry still owes this client its side-effects. */
const transientDeskFailures = new Set<string>();

/**
 * THE desk create-and-pay (tv-2na.8): a fresh booking + its paid deposit (+ an
 * optional brand-new customer) land through ONE create-and-pay intent — never the
 * old upsertBooking + recordManualPayment({bookingId}) pair of PUTs that
 * deadlocked on the circular booking <-> deposit FK and was silently dropped while
 * the UI, SMS and waiver said it worked. Used by the agent's card-over-phone and
 * the walk-in in-shop deposit (DepositLinkModal), the Sweeper gap-fill card by
 * phone (gapFillFlow), the console walk-in (walkInFlow) and NewBookingModal.
 *
 * Nothing is written to the store before the server answers; on ok the records
 * are mirrored with the bridge PUTs suppressed, the deposit-level side-effects
 * (stat, activity, receipt) run, then the caller's `onConfirmed`. A slot the store
 * already knows is firm-taken (overlapsFirm, the DB's exact rule) is refused
 * locally as slot_taken without a POST.
 */
export function createAndPayAtDesk(input: DeskCreateAndPayInput): Promise<DeskCreateAndPayResult> {
  const brain = useBrainStore.getState();
  const at = nowIso();
  const note = MANUAL_METHOD_LABEL[input.method];
  const depositId = input.depositId ?? `dep-manual-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
  const bookingId = input.booking.id;

  const deposit: Deposit = {
    id: depositId,
    customerId: input.booking.customerId,
    studioId: input.booking.studioId,
    artistId: input.booking.artistId,
    amountAud: input.amountAud,
    amountPaid: input.amountAud,
    feesAud: 0,
    netAud: input.amountAud,
    status: 'paid',
    channelSent: input.channelSent ?? 'walk-in',
    stripePaymentLinkId: 'manual',
    method: input.method,
    expiresAt: at,
    createdAt: at,
    paidAt: at,
    policy: 'seven-day-rule',
    source: input.booking.source,
    bookingId,
    ledger: [
      { at, amountAud: input.amountAud, kind: 'paid', by: input.by, note, ...(input.channel ? { channel: input.channel } : {}) },
    ],
    ...(currentAgentId() ? { takenByAgentId: currentAgentId() } : {}),
  };
  // Exactly what the server writes: SOLID (confirmed + not over-bookable => is_firm),
  // cross-refs the deposit, and NO confirmedAt (the cadence owns it — tv-xue).
  // An arrived (checked-in / in-chair) booking keeps its lifecycle — the server's
  // payBookingEffect 'firm-in-place' (tv-2na.8.15 / 11.20).
  const { confirmedAt: _stripped, ...bookingRest } = input.booking;
  const confirmed: Booking =
    payBookingEffect(input.booking.status) === 'firm-in-place'
      ? { ...input.booking, overbookable: false, depositId }
      : { ...bookingRest, status: 'confirmed', overbookable: false, depositId };

  if (input.newPiece) confirmed.pieceId = newPieceIdFor(depositId);
  const createdPiece = input.newPiece ? newPieceRecord(input.newPiece, depositId, confirmed) : undefined;

  const fail = (f: IntentFailure): DeskCreateAndPayFailure => {
    const failure = { ...f, bookingId, depositId };
    if (f.kind === 'transient') transientDeskFailures.add(depositId);
    input.onFailed?.(failure);
    return failure;
  };

  // A brand-new customer carries the form's email (D05-11) when it has none.
  const newCustomer =
    input.customer && input.contactEmail && !input.customer.email
      ? { ...input.customer, email: input.contactEmail }
      : input.customer;

  const confirm = (alreadyExists: boolean) => {
    const mirror = () => {
      if (newCustomer) brain.upsertCustomer(newCustomer);
      if (createdPiece) brain.upsertPiece(createdPiece);
      brain.upsertBooking(confirmed);
      brain.upsertDeposit(deposit);
    };
    if (isStoreFallbackEnabled()) mirror();
    else
      withServerAuthoredWrites(
        [
          ...(input.customer ? ([['customers', input.customer.id]] as const) : []),
          ...(createdPiece ? ([['pieces', createdPiece.id]] as const) : []),
          ['bookings', bookingId],
          ['deposits', depositId],
        ],
        mirror,
      );
    // The intent booked the lead; mirror it BEFORE the side-effects below write
    // the customer (the receipt's numberOnFile), so that write carries it.
    mirrorLeadStageBooked(confirmed.customerId);
    if (!newCustomer) mirrorCapturedEmail(confirmed.customerId, input.contactEmail);
    // A replay of a create THIS client already confirmed ran the side-effects;
    // one whose first response was lost (transient) did not — run them now.
    const owed = !alreadyExists || transientDeskFailures.has(depositId);
    transientDeskFailures.delete(depositId);
    if (owed) {
      brain.incStat('deposits');
      brain.appendActivity({
        id: `act-manualpay-${bookingId}-${depositId}`,
        type: 'deposit-paid',
        customerId: confirmed.customerId,
        studioId: confirmed.studioId,
        artistId: confirmed.artistId,
        amount: input.amountAud,
        body: `Manual payment ${formatAud(input.amountAud)} (${note}) recorded on ${confirmed.customerId}'s booking.`,
        timestamp: at,
      });
      emitDepositReceipt(deposit, input.amountAud, at);
      input.onConfirmed?.({ bookingId, depositId });
    }
    return { ok: true as const, bookingId, depositId, alreadyExists };
  };

  // The DB's exact double-book rule against what this client already knows: no
  // point POSTing a slot a firm booking already holds.
  const clash = overlapsFirm(
    {
      artistId: confirmed.artistId,
      startIso: confirmed.start,
      endIso: new Date(new Date(confirmed.start).getTime() + confirmed.durationMin * 60000).toISOString(),
      excludeBookingId: bookingId,
    },
    brain.bookings,
  );
  if (clash) {
    return Promise.resolve(
      fail({ ok: false, kind: 'slot_taken', status: 409, error: 'booking-conflict', code: 'slot_taken' }),
    );
  }

  // STORE-FALLBACK (unit tests / standalone): no server — land it synchronously.
  if (isStoreFallbackEnabled()) return Promise.resolve(confirm(false));

  return createAndPayIntent({
    bookingId,
    depositId,
    booking: confirmed,
    deposit,
    payment: {
      amountAud: input.amountAud,
      ...(input.by ? { by: input.by } : {}),
      note,
      ...(input.channel ? { channel: input.channel } : {}),
      at,
    },
    ...(newCustomer ? { customer: newCustomer } : {}),
    ...(input.newPiece ? { newPiece: input.newPiece } : {}),
    ...(input.contactEmail ? { contactEmail: input.contactEmail } : {}),
  })
    .then((res): DeskCreateAndPayResult => (res.ok ? confirm(res.alreadyExists || res.alreadyPaid) : fail(res)))
    .catch((err): DeskCreateAndPayResult => {
      console.error('[create-and-pay] desk payment failed', err);
      return fail({ ok: false, kind: 'transient', status: 0, error: 'network' });
    });
}

/**
 * D05-11: the server filled an empty email from the take-payment form in the
 * pay's own transaction; mirror it into the store with no bridge PUT.
 */
export function mirrorCapturedEmail(customerId: string, contactEmail: string | undefined): void {
  if (!contactEmail) return;
  const b = useBrainStore.getState();
  const c = b.customers[customerId];
  if (!c || (c.email ?? '').trim()) return;
  const apply = () => b.upsertCustomer({ ...c, email: contactEmail });
  if (isStoreFallbackEnabled()) apply();
  else withServerAuthoredWrites([['customers', customerId]], apply);
}

/** What is still owed on a deposit — the amount a pay charges (mirrors the
 *  server's runPayDeposit: the larger of the ledger net and amountPaid is in). */
function owedOnDeposit(d: Deposit): number {
  return Math.max(0, d.amountAud - Math.max(depositPaidAud(d), d.amountPaid ?? 0));
}

/**
 * The booking a pay on this deposit firms — the SAME booking the server's
 * runPayDeposit resolves (`heldBookingId ?? bookingId`): a held placeholder
 * (hold-slot send) OR a booking the deposit is linked to by bookingId (a
 * `pending-deposit` booking whose link is still open, e.g. part-paid). Undefined
 * = no live booking to firm (dateless on file / a fresh slot is created instead).
 */
function payTargetBooking(
  d: Deposit,
  bookings: Record<string, Booking>,
): { booking: Booking; effect: Exclude<PayBookingEffect, 'refuse'> } | undefined {
  const id = d.heldBookingId ?? d.bookingId;
  const b = id ? bookings[id] : undefined;
  if (!b) return undefined;
  // The ONE pay rule (@atink/domain payBookingEffect, tv-2na.8.15), shared with
  // the server's runPayDeposit: pre-arrival -> firm to confirmed; checked-in /
  // in-chair -> the deposit settles but the booking is LEFT as it is; a
  // finished or dead booking is never a pay target.
  const effect = payBookingEffect(b.status);
  return effect === 'refuse' ? undefined : { booking: b, effect };
}

/**
 * Mirror the pay intents' in-transaction lead-stage advance (tv-2na.8.12,
 * @atink/domain advanceLeadStage: forward-only to 'booked') onto the store's
 * customer. The server writes it inside runPayDeposit / runCreateAndPay /
 * runPayTopupInstalment, but the confirm side-effects that follow (the booking
 * invite / waiver conversation entries) write the WHOLE customer back through the
 * bridge before the doorbell refresh lands — with the stale stage, undoing the
 * advance. Mirrored as a server-authored write (no PUT of its own) so every later
 * customer write carries 'booked'. Store-fallback: a normal write.
 */
export function mirrorLeadStageBooked(customerId: string): void {
  const booked = leadStageBookedCustomer(customerId);
  if (!booked) return;
  const apply = () => useBrainStore.getState().upsertCustomer(booked);
  if (isStoreFallbackEnabled()) apply();
  else withServerAuthoredWrites([['customers', customerId]], apply);
}

/** The store customer advanced to 'booked' (forward-only), or undefined when the
 *  customer is unknown or already at/after 'booked'. For a caller folding the
 *  advance into its own server-authored mirror (the top-up pay). */
export function leadStageBookedCustomer(customerId: string): CustomerRecord | undefined {
  const customer = useBrainStore.getState().customers[customerId];
  if (!customer) return undefined;
  const next = advanceLeadStage(customer.leadStage, 'booked') as CustomerRecord['leadStage'];
  return next === customer.leadStage ? undefined : { ...customer, leadStage: next };
}

/**
 * Money already on a still-`pending` deposit: a ledger / amountPaid balance, or a
 * paid instalment on its top-up plan. Such a deposit is part-paid, not an unpaid
 * link (a $50+ partial is a REAL booking — board 02; tv-2na.8.10).
 */
export function hasMoneyIn(deposit: Deposit, topupPlans: Record<string, TopupPlan>): boolean {
  if (depositPaidAud(deposit) > 0 || (deposit.amountPaid ?? 0) > 0) return true;
  return hasPaidTopupInstalment(deposit.id, topupPlans);
}

/** A paid instalment on any top-up plan attached to this deposit. */
function hasPaidTopupInstalment(depositId: string, topupPlans: Record<string, TopupPlan>): boolean {
  return Object.values(topupPlans).some(
    (p) => p.depositId === depositId && p.instalments.some((i) => i.status === 'paid'),
  );
}

/**
 * Expire pending deposits whose window has elapsed: mark the deposit `expired`,
 * release the held placeholder booking (status -> cancelled, freeing the live
 * calendar slot) and retain both records so an expired link stays findable and
 * re-sendable. Never sets `bookingId` (the hold never locked). Mirrors
 * reconcileSequenceClock; "now" is the sim clock.
 *
 * The rule is the shared @atink/domain isLinkSweepable (the server sweep applies
 * the same one): a pay-at-studio hold, a part-paid deposit (board 02;
 * tv-2na.8.10 — a missed top-up is the Agent's 3-sequence chase, not a link
 * lapse) and a link whose client has already arrived are all skipped.
 *
 * This is the STORE-FALLBACK sweep (unit tests / standalone). The served build
 * sweeps on the server — call sweepExpiredLinks, never this, from a trigger.
 */
export function reconcileDepositHolds(): void {
  const brain = useBrainStore.getState();
  const now = nowIso();
  for (const deposit of Object.values(brain.deposits)) {
    const targetId = deposit.heldBookingId ?? deposit.bookingId;
    const target = targetId ? brain.bookings[targetId] : undefined;
    const sweepable = isLinkSweepable(
      {
        status: deposit.status,
        expiresAt: deposit.expiresAt,
        amountAud: deposit.amountAud,
        ...(deposit.amountPaid !== undefined ? { amountPaid: deposit.amountPaid } : {}),
        ...(deposit.ledger ? { ledger: deposit.ledger } : {}),
        ...(deposit.payAtStudio ? { payAtStudio: true } : {}),
      },
      now,
      {
        hasPaidInstalment: Object.values(brain.topupPlans).some(
          (p) =>
            p.depositId === deposit.id &&
            planProtectsLink(
              { status: p.status, hasPaidInstalment: p.instalments.some((i) => i.status === 'paid') },
              (p.bookingId ? brain.bookings[p.bookingId] : target)?.status,
            ),
        ),
        ...(target ? { bookingStatus: target.status } : {}),
      },
    );
    if (!sweepable) continue;
    brain.upsertDeposit({ ...deposit, status: 'expired' });
    // The ONE slot key (7w8f): a link that points at its slot only by bookingId frees it too.
    const held = targetId ? brain.bookings[targetId] : undefined;
    if (held && held.status === 'held') {
      brain.upsertBooking({ ...held, status: 'cancelled' });
    }
    brain.appendActivity(expiredActivity(deposit, now));
  }
}

function expiredActivity(deposit: Deposit, at: string) {
  return {
    id: `act-dep-expired-${deposit.id}`,
    type: 'deposit-expired' as const,
    customerId: deposit.customerId,
    studioId: deposit.studioId,
    artistId: deposit.artistId,
    amount: deposit.amountAud,
    body: `Deposit link expired for ${deposit.customerId}. Held slot released; record kept.`,
    timestamp: at,
  };
}

/** One link the server sweep expired (POST /api/intents/sweep-links). */
interface SweptLink {
  depositId: string;
  releasedBookingId?: string;
}

export type SweepLinksOutcome = { ok: true; expired: string[] } | { ok: false; error: string };

/**
 * Run the deposit-link expiry sweep NOW, on the facilitator's clock
 * (tv-2na.10.11). The demo's triggers — a forward header day-step, the console
 * "deposit-sweep" and Age-the-cohort — call this; nothing runs it on wall time
 * (the demo clock lives in the browser, DEMO_NOW 2026-05-04).
 *
 * Served build: the SERVER sweeps (packages/db runExpireDepositLinks, one small
 * transaction per link) and this mirrors exactly what it expired into the store,
 * marked server-authored so no PUT re-sends it. Pending writes are flushed first
 * so the sweep sees a link that was just paid or extended. Never throws.
 * Store-fallback build: the in-browser reconcileDepositHolds, synchronously.
 */
export function sweepExpiredLinks(): Promise<SweepLinksOutcome> {
  if (isStoreFallbackEnabled()) {
    const before = useBrainStore.getState().deposits;
    reconcileDepositHolds();
    const after = useBrainStore.getState().deposits;
    const expired = Object.keys(after).filter((id) => after[id]!.status === 'expired' && before[id]?.status !== 'expired');
    return Promise.resolve({ ok: true, expired });
  }
  const at = nowIso();
  return flushWritesAndWait()
    .then(() =>
      fetch('/api/intents/sweep-links', {
        method: 'POST',
        headers: { 'content-type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ nowIso: at }),
      }),
    )
    .then(async (res): Promise<SweepLinksOutcome> => {
      if (!res.ok) return { ok: false, error: `http-${res.status}` };
      const body = (await res.json()) as { expired?: SweptLink[] };
      const swept = body.expired ?? [];
      mirrorSweptLinks(swept, at);
      return { ok: true, expired: swept.map((e) => e.depositId) };
    })
    .catch((err): SweepLinksOutcome => {
      console.error('[sweep-links] failed', err);
      return { ok: false, error: 'network' };
    });
}

function mirrorSweptLinks(swept: readonly SweptLink[], at: string): void {
  if (swept.length === 0) return;
  const pairs: Array<readonly [string, string]> = [];
  for (const e of swept) {
    pairs.push(['deposits', e.depositId]);
    if (e.releasedBookingId) pairs.push(['bookings', e.releasedBookingId]);
  }
  withServerAuthoredWrites(pairs, () => {
    const brain = useBrainStore.getState();
    for (const e of swept) {
      const deposit = brain.deposits[e.depositId];
      if (!deposit) continue;
      brain.upsertDeposit({ ...deposit, status: 'expired' });
      const held = e.releasedBookingId ? brain.bookings[e.releasedBookingId] : undefined;
      if (held && held.status === 'held') brain.upsertBooking({ ...held, status: 'cancelled' });
      brain.appendActivity(expiredActivity(deposit, at));
    }
  });
}

function sessionLengthForAmount(amountAud: number): Booking['sessionLength'] {
  if (amountAud >= 500) return 'full-day';
  if (amountAud >= 300) return 'half-day';
  return 'small';
}

/**
 * The session tier to re-mint a resent deposit link at. Per-artist deposits
 * (tv-ynl) break the flat amount→tier inference — the same $ can be a half-day
 * for one artist and a full-day for another — so prefer any EXPLICIT session the
 * record already carries: the linked booking's own `sessionLength`, else the
 * held slot's duration (artist-independent). Amount-inference stays only as the
 * last resort when neither is known. Exported for direct testing.
 */
export function resendSessionLength(
  old: Pick<Deposit, 'amountAud' | 'bookingId'>,
  slot: { startIso: string; endIso: string } | undefined,
  bookings: Record<string, Booking>,
): Booking['sessionLength'] {
  const booked = old.bookingId ? bookings[old.bookingId]?.sessionLength : undefined;
  if (booked) return booked;
  if (slot) {
    const minutes = (new Date(slot.endIso).getTime() - new Date(slot.startIso).getTime()) / 60000;
    if (minutes > 0) return sessionLengthForDuration(minutes);
  }
  return sessionLengthForAmount(old.amountAud);
}

function castLinkChannel(c: string): 'sms' | 'meta-dm' | 'ig-dm' | 'email' {
  if (c === 'sms' || c === 'meta-dm' || c === 'ig-dm' || c === 'email') return c;
  return 'sms';
}

/** A deposit is effectively expired once its window has elapsed, even if a
 * reconcile pass has not yet flipped the persisted `status` field. */
export function isEffectivelyExpired(deposit: Deposit): boolean {
  if (deposit.status === 'expired') return true;
  // A pay-at-studio hold never runs on the link clock, so a pending one is never
  // "effectively expired" regardless of its window.
  if (deposit.payAtStudio) return false;
  // Nor is a part-paid one: the link expiry never applies once money is in (tv-2na.8.10).
  if (depositPaidAud(deposit) > 0) return false;
  return (
    deposit.status === 'pending' &&
    new Date(deposit.expiresAt).getTime() < new Date(nowIso()).getTime()
  );
}

/**
 * Resend / follow up on a deposit link. Two shapes are supported so both the
 * A+B (office / queue / ledger) and C (self-book follow-up) flows are preserved:
 *
 * - Object form `{ depositId, discussedSlot?, channel? }`: for an effectively
 *   expired record, mint a FRESH link + hold (reusing the old slot unless a new
 *   one is supplied), stamp the old record `supersededByDepositId` (so it reads
 *   "link resent") and `resentAt`, and return the fresh Deposit. The old record
 *   is retained and keeps its expired state. No-op (undefined) unless the source
 *   has effectively expired.
 *
 * - String form `resendDepositLink(depositId)`: non-destructive follow-up that
 *   flips the SAME record to a fresh 48h window and stamps `resentAt` so it
 *   surfaces as "link resent" in the ledger and the artist status list. Never
 *   deletes; never touches a paid record.
 */
export function resendDepositLink(
  arg:
    | string
    | {
        depositId: string;
        discussedSlot?: { artistId: string; startIso: string; endIso: string };
        channel?: 'sms' | 'meta-dm' | 'ig-dm' | 'email';
      },
): Deposit | undefined {
  const brain = useBrainStore.getState();

  // String form (C8): flip the same record to a fresh 48h window in place.
  if (typeof arg === 'string') {
    const deposit = brain.deposits[arg];
    if (!deposit) return undefined;
    if (deposit.status === 'paid') return undefined;
    // A deposit link always carries a date (7g3y): an older dateless link gets no fresh window.
    if (!slotBookingId(deposit)) return undefined;
    const now = nowIso();
    const expiresAt = new Date(new Date(now).getTime() + 48 * 60 * 60 * 1000).toISOString();
    const updated: Deposit = { ...deposit, status: 'pending', resentAt: now, expiresAt };
    brain.upsertDeposit(updated);
    brain.appendActivity({
      id: `act-dep-resent-${deposit.id}-${Date.now()}`,
      type: 'deposit-link-sent',
      customerId: deposit.customerId,
      studioId: deposit.studioId,
      artistId: deposit.artistId,
      body: `Deposit link ${deposit.depositReference ?? deposit.id} resent. Fresh 48h window.`,
      timestamp: now,
    });
    return updated;
  }

  // Object form (A+B): mint a fresh link + hold for an expired record and mark
  // the old one superseded (retained, still expired).
  const old = brain.deposits[arg.depositId];
  if (!old || !isEffectivelyExpired(old)) return undefined;
  const slot = arg.discussedSlot ?? old.discussedSlot;
  // A fresh link always carries a date (7g3y): with none, nothing is minted.
  if (!slot) return undefined;
  const fresh = generateDepositLink({
    customerId: old.customerId,
    studioId: old.studioId,
    artistId: old.artistId ?? 'pat',
    amountAud: old.amountAud,
    channel: arg.channel ?? castLinkChannel(old.channelSent),
    sessionLength: resendSessionLength(old, slot, brain.bookings),
    source: old.source as Booking['source'],
    discussedSlot: slot,
    ...(old.pieceName ? { pieceName: old.pieceName } : {}),
  });
  brain.upsertDeposit({ ...old, supersededByDepositId: fresh.id, resentAt: nowIso() });
  return fresh;
}

// ─── One honest resend (W5b #2, tv-2na.8.20 / 8.19) ─────────────────────────

/** What "Pick a new date" opens with (Q1): the old link's artist / session / amount. */
export interface ResendPrefill {
  customerId: string;
  artistId?: string;
  sessionLength: Booking['sessionLength'];
  amountAud: number;
  pieceName?: string;
}

/**
 * The result a resend surface switches on. Only `ok` may toast success; nothing
 * is written to the store for any other outcome (the D21 fix: the old code
 * stamped the old link "resent" before the server answered).
 */
export type ResendOutcome =
  | { outcome: 'ok'; freshDepositId: string; route?: DepositLinkRoute }
  | { outcome: 'needs-new-date'; reason: NewDateReason; prefill: ResendPrefill }
  | { outcome: 'not-resendable'; reason: string }
  | { outcome: 'already-resent' }
  | { outcome: 'failed'; failure: IntentFailure };

const RESEND_DEAD_BOOKING = new Set<Booking['status']>(['cancelled', 'no-show', 'superseded']);

/** The slot an old link held: its discussed slot, else its (released) ghost's window. */
function oldLinkSlot(
  old: Deposit,
  bookings: Record<string, Booking>,
): { artistId: string; startIso: string; endIso: string } | undefined {
  if (old.discussedSlot) return old.discussedSlot;
  const ghost = old.heldBookingId ? bookings[old.heldBookingId] : undefined;
  if (!ghost) return undefined;
  return {
    artistId: ghost.artistId,
    startIso: ghost.start,
    endIso: new Date(new Date(ghost.start).getTime() + ghost.durationMin * 60_000).toISOString(),
  };
}

function slotTakenLocally(
  slot: { artistId: string; startIso: string; endIso: string },
  bookings: Record<string, Booking>,
  ownBookingId: string | undefined,
): boolean {
  const s = Date.parse(slot.startIso);
  const e = Date.parse(slot.endIso);
  return Object.values(bookings).some((b) => {
    if (b.id === ownBookingId || b.artistId !== slot.artistId || RESEND_DEAD_BOOKING.has(b.status)) return false;
    const bs = Date.parse(b.start);
    return bs < e && bs + b.durationMin * 60_000 > s;
  });
}

/**
 * Re-send an expired, unpaid deposit link: ONE decision (@atink/domain
 * resendDecision, the same rule the server re-runs under the deposit lock) and,
 * served, ONE intent (POST /api/intents/resend-link) that mints the fresh link
 * AND supersedes the old one, or nothing. The store is written only once the
 * server says ok — no optimistic "resent" stamp to revert.
 *
 * - `sentByRole`: the resending surface's role (Q3: the resender owns the
 *   follow-up), else the work context / session persona.
 * - `via: 'sweeper'`: the Sweeper's Expired rung may re-offer past the Q2 window.
 * A past or taken slot resolves to `needs-new-date` with the prefill for
 * "Pick a new date" (Q1); the old row stays Expired.
 */
export async function resendExpiredLink(opts: {
  depositId: string;
  sentByRole?: LinkSenderRole;
  via?: ResendVia;
  channel?: 'sms' | 'meta-dm' | 'ig-dm' | 'email';
  /** No person pressed resend (queue routing): outside 24h it writes the note, no message-box draft. */
  automatic?: boolean;
}): Promise<ResendOutcome> {
  const brain = useBrainStore.getState();
  const old = brain.deposits[opts.depositId];
  if (!old) return { outcome: 'not-resendable', reason: 'not-found' };
  const now = nowIso();
  const slot = oldLinkSlot(old, brain.bookings);
  // The old ghost knows the tier it held; else the shared resend inference.
  const oldGhostSession = old.heldBookingId ? brain.bookings[old.heldBookingId]?.sessionLength : undefined;
  const prefill: ResendPrefill = {
    customerId: old.customerId,
    ...(slot?.artistId ?? old.artistId ? { artistId: slot?.artistId ?? old.artistId } : {}),
    sessionLength: oldGhostSession ?? resendSessionLength(old, slot, brain.bookings),
    amountAud: old.amountAud,
    ...(old.pieceName ? { pieceName: old.pieceName } : {}),
  };
  if (hasMoneyIn(old, brain.topupPlans)) return { outcome: 'not-resendable', reason: 'money-in' };
  const decision = resendDecision({
    deposit: {
      status: old.status,
      expiresAt: old.expiresAt,
      amountAud: old.amountAud,
      amountPaid: depositPaidAud(old),
      ...(old.supersededByDepositId ? { supersededByDepositId: old.supersededByDepositId } : {}),
      ...(old.payAtStudio ? { payAtStudio: true } : {}),
      ...(old.ledger ? { ledger: old.ledger } : {}),
    },
    ...(slot ? { slot } : {}),
    slotTaken: slot ? slotTakenLocally(slot, brain.bookings, old.heldBookingId) : false,
    nowIso: now,
    ...(opts.via ? { via: opts.via } : {}),
  });
  if (decision.kind === 'pick-new-date') return { outcome: 'needs-new-date', reason: decision.reason, prefill };
  if (decision.kind === 'not-resendable') return { outcome: 'not-resendable', reason: decision.reason };

  // ── Build the fresh pair (the old link's terms, the RESENDER's name on it) ──
  const id = seedId('dep') ?? `dep-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
  const firm = old.source === 'gap-fill';
  const expiresAt = slot
    ? holdExpiresAtIso(slot.startIso, now, { firm })
    : new Date(new Date(now).getTime() + 48 * 60 * 60 * 1000).toISOString();
  const heldBookingId = slot ? `bk-${id}` : undefined;
  const sentByRole = resolveResender(old.customerId, opts.sentByRole);
  const sentBy = currentActorId();
  const agentId = currentAgentId();
  // B7 (tv-fton): the fresh pair sits at the ARTIST's studio, like a new link. A
  // link filed under the customer's studio before the studio check would copy
  // it and the server would refuse the hold (it restamps too).
  const freshArtistId = slot?.artistId ?? old.artistId;
  const artistStudioId = freshArtistId ? brain.artists[freshArtistId]?.studioId : undefined;
  const fresh: Deposit = {
    id,
    customerId: old.customerId,
    studioId: artistStudioId ?? old.studioId,
    ...(slot?.artistId ?? old.artistId ? { artistId: slot?.artistId ?? old.artistId } : {}),
    amountAud: old.amountAud,
    feesAud: old.feesAud,
    netAud: old.netAud,
    status: 'pending',
    channelSent: opts.channel ?? castLinkChannel(old.channelSent),
    stripePaymentLinkId: `plink_mock_${id}`,
    expiresAt,
    createdAt: now,
    policy: old.policy,
    source: old.source,
    ...(agentId ? { takenByAgentId: agentId } : {}),
    ...(sentByRole ? { sentByRole } : {}),
    ...(sentByRole && sentBy ? { sentBy } : {}),
    ...(slot ? { discussedSlot: slot } : {}),
    ...(heldBookingId ? { heldBookingId } : {}),
    ...(old.pieceName ? { pieceName: old.pieceName } : {}),
    ...(old.depositReference ? { depositReference: old.depositReference } : {}),
  };
  const ghost = old.heldBookingId ? brain.bookings[old.heldBookingId] : undefined;
  const held: Booking | undefined =
    slot && heldBookingId
      ? {
          id: heldBookingId,
          customerId: old.customerId,
          studioId: artistStudioId ?? ghost?.studioId ?? old.studioId,
          artistId: slot.artistId,
          start: slot.startIso,
          durationMin: Math.max(15, Math.round((Date.parse(slot.endIso) - Date.parse(slot.startIso)) / 60000)),
          sessionLength: prefill.sessionLength,
          status: 'held',
          depositId: id,
          ...(!firm && isThisWeek(slot.startIso, now) ? { overbookable: true } : {}),
          ...(ghost?.confirmedOnCall ? { confirmedOnCall: true } : {}),
          designBrief: ghost?.designBrief ?? old.pieceName ?? 'Held on deposit link.',
          referencePhotos: ghost?.referencePhotos ?? [],
          source: ghost?.source ?? (old.source as Booking['source']),
          createdAt: now,
          ...(ghost?.placement ? { placement: ghost.placement } : {}),
          ...(ghost?.bodyPart ? { bodyPart: ghost.bodyPart } : {}),
          ...(ghost?.bodyPartSide ? { bodyPartSide: ghost.bodyPartSide } : {}),
          ...(ghost?.bodyPartQualifier ? { bodyPartQualifier: ghost.bodyPartQualifier } : {}),
          ...(ghost?.styleTags?.length ? { styleTags: ghost.styleTags } : {}),
          ...(ghost?.colour ? { colour: ghost.colour } : {}),
          ...(ghost?.collabArtistIds?.length ? { collabArtistIds: ghost.collabArtistIds } : {}),
          ...(ghost?.pieceId ? { pieceId: ghost.pieceId } : {}),
          ...(ghost?.dayRateAud != null ? { dayRateAud: ghost.dayRateAud } : {}),
        }
      : undefined;

  // How the fresh link reached the customer (tv-isee), set by logResend.
  let route: DepositLinkRoute = 'local';
  const mirror = () => {
    const b = useBrainStore.getState();
    b.upsertDeposit(fresh);
    if (held) b.upsertBooking(held);
    const current = b.deposits[old.id] ?? old;
    b.upsertDeposit({ ...current, status: 'expired', supersededByDepositId: id, resentAt: now });
    const oldGhost = old.heldBookingId ? b.bookings[old.heldBookingId] : undefined;
    if (oldGhost?.status === 'held') b.upsertBooking({ ...oldGhost, status: 'cancelled' });
    route = logResend(old, fresh, now, prefill.sessionLength, !opts.automatic);
  };

  if (isStoreFallbackEnabled()) {
    mirror();
    return { outcome: 'ok', freshDepositId: id, route };
  }

  const res = await resendLinkIntent({
    oldDepositId: old.id,
    depositId: id,
    deposit: fresh,
    ...(held && heldBookingId ? { bookingId: heldBookingId, booking: held } : {}),
    ...(opts.via ? { via: opts.via } : {}),
  });
  if (res.outcome === 'ok') {
    const pairs: Array<readonly [string, string]> = [['deposits', id], ['deposits', old.id]];
    if (heldBookingId) pairs.push(['bookings', heldBookingId]);
    if (old.heldBookingId) pairs.push(['bookings', old.heldBookingId]);
    withServerAuthoredWrites(pairs, mirror);
    return { outcome: 'ok', freshDepositId: res.freshDepositId, route };
  }
  if (res.outcome === 'needs-new-date') return { outcome: 'needs-new-date', reason: res.reason, prefill };
  if (res.outcome === 'not-resendable') return { outcome: 'not-resendable', reason: res.reason };
  if (res.outcome === 'already-resent') return { outcome: 'already-resent' };
  return { outcome: 'failed', failure: res.failure };
}

/**
 * The feed line + the client's timeline card for a landed resend. The card goes
 * through deliverDepositMessage (tv-isee): on a live Instagram thread the customer
 * gets the link as a DM and the "Resent…" card is only the local/failure note.
 * B6 (75ng): when it would only be a local text, the served build writes nothing
 * here: the resend-link intent texts the fresh link itself (a server row carrying
 * the depositId, so the timeline still shows its card), and a client line would
 * text the client twice. Store fallback has no server, so it keeps the note.
 */
function logResend(old: Deposit, fresh: Deposit, at: string, sessionLength: SessionLength, handoff: boolean): DepositLinkRoute {
  const brain = useBrainStore.getState();
  brain.appendActivity({
    id: `act-${fresh.id}`,
    type: 'deposit-link-sent',
    customerId: fresh.customerId,
    studioId: fresh.studioId,
    ...(fresh.artistId ? { artistId: fresh.artistId } : {}),
    amount: fresh.amountAud,
    body: `Deposit link ${old.depositReference ?? old.id} resent as a fresh link. ${formatAud(fresh.amountAud)}.`,
    timestamp: at,
  });
  const customer = brain.customers[fresh.customerId];
  if (fresh.customerId === AWAITING_CLIENT || !customer) return 'local';
  const nowWall = new Date().toISOString();
  if (!isStoreFallbackEnabled() && depositMessageRoute({ customer, channel: fresh.channelSent, nowIso: nowWall }) === 'local') {
    return 'local';
  }
  const url = customerDepositUrl(fresh.id);
  return deliverDepositMessage({
    customer,
    channel: fresh.channelSent,
    text: depositResendMessage({ firstName: customer.firstName, sessionLength, amountAud: formatAud(fresh.amountAud), url }),
    depositId: fresh.id,
    kind: 'resend',
    handoff,
    localNote: {
      id: `convo-dep-${fresh.id}`,
      customerId: fresh.customerId,
      channel: fresh.channelSent,
      direction: 'out',
      body: `Resent the ${formatAud(fresh.amountAud)} deposit link: ${url}`,
      // A real HookChat thread runs on wall time; the sim clock would sort this above the real DMs.
      timestamp: fresh.channelSent === 'ig-dm' ? windowNowIso(customer, at) : at,
      authorRole: 'agent',
      authorName: 'You',
      depositId: fresh.id,
    },
    nowIso: nowWall,
  });
}

export function generateSelfBookLink(
  input: Omit<SelfBookLink, 'token' | 'generatedAt' | 'status' | 'depositReference'> & {
    depositReference?: string;
  },
): SelfBookLink {
  const token = Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 6);
  const link: SelfBookLink = {
    ...input,
    token,
    status: 'active',
    generatedAt: nowIso(),
    // Staff-sent links get a generic minted code; universal links pass their own
    // artist-derived reference (AL-ASHER-4F7K) via selfBookFlow.
    depositReference: input.depositReference ?? `REF-${token.slice(0, 6).toUpperCase()}`,
  };
  const brain = useBrainStore.getState();
  // B5 (8xar): served, a NAMED link is sent through the server, which stamps its
  // clock and chains it to the client's expired link (a fresh link, never the old
  // one). Mirrored now (no bridge PUT); a refusal takes it back with a toast.
  if (!isStoreFallbackEnabled() && link.customerId && !link.universal) {
    withServerAuthoredWrites([['selfBookLinks', token]], () => brain.upsertSelfBookLink(link));
    void sendSelfBookLinkIntent(link as unknown as Record<string, unknown>).then((res) => {
      const b = useBrainStore.getState();
      if (!res.ok) {
        withServerAuthoredWrites([['selfBookLinks', token]], () =>
          useBrainStore.setState((st) => {
            const selfBookLinks = { ...st.selfBookLinks };
            delete selfBookLinks[token];
            return { selfBookLinks };
          }),
        );
        useUIStore.getState().pushNotification({
          intensity: 'critical',
          title: "The link wasn't sent",
          body: res.message ?? 'Try sending it again.',
          ttlMs: 8000,
        });
        return;
      }
      const current = selfBookLinkByToken(b.selfBookLinks, token) ?? link;
      const prior = res.replacesLinkId ? selfBookLinkByToken(b.selfBookLinks, res.replacesLinkId) : undefined;
      withServerAuthoredWrites(
        [
          ['selfBookLinks', selfBookLinkKey(b.selfBookLinks, token)],
          ...(prior ? ([['selfBookLinks', selfBookLinkKey(b.selfBookLinks, prior.token)]] as const) : []),
        ],
        () => {
          b.upsertSelfBookLink({
            ...current,
            generatedAt: res.generatedAt || current.generatedAt,
            expiresAt: res.expiresAt || current.expiresAt,
            ...(res.replacesLinkId ? { replacesLinkId: res.replacesLinkId } : {}),
          });
          if (prior) b.upsertSelfBookLink({ ...prior, replacedByLinkId: token });
        },
      );
    });
  } else {
    // Store-fallback: chain the fresh link to the client's expired one, as the
    // server does, so the Agent's "expired" alert clears once it is sent.
    const prior =
      link.customerId && !link.universal
        ? Object.values(brain.selfBookLinks)
            .filter((l) => l.customerId === link.customerId && !l.universal && l.status === 'expired' && !l.replacedByLinkId)
            .sort((a, b) => Date.parse(b.expiresAt) - Date.parse(a.expiresAt))[0]
        : undefined;
    brain.upsertSelfBookLink(prior ? { ...link, replacesLinkId: prior.token } : link);
    if (prior) brain.upsertSelfBookLink({ ...prior, replacedByLinkId: token });
  }
  brain.appendActivity({
    id: `act-link-${token}`,
    type: 'self-book-link-sent',
    customerId: input.customerId,
    studioId: input.studioId,
    artistId: input.artistId,
    body: `Self-book link sent to ${input.customerId} via ${input.channelSent}. Expires ${input.expiresAt.slice(0, 16)}.`,
    timestamp: nowIso(),
  });
  return link;
}

/**
 * Forfeit a paid deposit whose booking was a no-show (G2, 14 Jul + 30 Jun 8h
 * rule). Writes a `forfeit` movement that zeroes the paid balance; the deposit
 * stays linked to the booking so it reads as `lapsed` in the ledger. Idempotent.
 * // ASSUMPTION: forfeit fires on no-show mark, uniform across shops, reversible.
 */
export function forfeitDepositForNoShow(
  bookingId: string,
  opts?: { by?: string; at?: string; note?: string },
): Deposit | undefined {
  const brain = useBrainStore.getState();
  const booking = brain.bookings[bookingId];
  if (!booking?.depositId) return undefined;
  const deposit = brain.deposits[booking.depositId];
  if (!deposit) return undefined;
  const paid = depositPaidAud(deposit);
  if (paid <= 0) return deposit; // nothing to forfeit / already forfeited
  const at = opts?.at ?? nowIso();
  // Preserve the paid history (backfill one entry when the deposit has none) so
  // the forfeit nets to zero rather than going negative.
  const baseLedger: DepositLedgerEntry[] =
    deposit.ledger && deposit.ledger.length > 0
      ? deposit.ledger
      : [
          {
            at: deposit.paidAt ?? at,
            amountAud: paid,
            kind: 'paid',
            by: deposit.takenByAgentId ?? 'system',
          },
        ];
  const updated: Deposit = {
    ...deposit,
    ledger: [
      ...baseLedger,
      {
        at,
        amountAud: paid,
        kind: 'forfeit',
        fromBookingId: bookingId,
        by: opts?.by ?? 'system',
        // L1 (17 Jul): the floor forfeit forces a typed reason; carry it onto the
        // ledger note so the deposit book surfaces why. Falls back to the fixed 8h
        // string for the automatic (rule-driven) forfeit path.
        note: opts?.note?.trim() || 'No-show forfeiture (8h rule)',
      },
    ],
  };
  brain.upsertDeposit(updated);
  return updated;
}

/**
 * Forfeit a paid deposit on an Ops CANCEL made inside the 7-day window (tv-bli).
 * Mirrors `forfeitDepositForNoShow`'s ledger write (a `forfeit` movement that
 * zeroes the paid balance, keeping the deposit linked so it reads as lapsed) but
 * carries a cancel-appropriate reason and, crucially, does NOT flip the booking
 * to `no-show`. The caller sets the booking to `cancelled`. Idempotent.
 */
export function forfeitDeposit(
  bookingId: string,
  opts?: { reason?: string; by?: string; at?: string },
): Deposit | undefined {
  const brain = useBrainStore.getState();
  const booking = brain.bookings[bookingId];
  if (!booking?.depositId) return undefined;
  const deposit = brain.deposits[booking.depositId];
  if (!deposit) return undefined;
  const paid = depositPaidAud(deposit);
  if (paid <= 0) return deposit; // nothing to forfeit / already forfeited
  const at = opts?.at ?? nowIso();
  // Preserve the paid history (backfill one entry when the deposit has none) so
  // the forfeit nets to zero rather than going negative.
  const baseLedger: DepositLedgerEntry[] =
    deposit.ledger && deposit.ledger.length > 0
      ? deposit.ledger
      : [
          {
            at: deposit.paidAt ?? at,
            amountAud: paid,
            kind: 'paid',
            by: deposit.takenByAgentId ?? 'system',
          },
        ];
  const updated: Deposit = {
    ...deposit,
    ledger: [
      ...baseLedger,
      {
        at,
        amountAud: paid,
        kind: 'forfeit',
        fromBookingId: bookingId,
        by: opts?.by ?? 'system',
        note: opts?.reason?.trim() || 'Cancelled inside 7-day window',
      },
    ],
    status: 'forfeited',
  };
  brain.upsertDeposit(updated);
  return updated;
}

/**
 * Reinstate a forfeited (lapsed) deposit (G2, management-only — gate at the call
 * site with `canReinstateLapsedDeposit`). Writes a reversing `reinstate`
 * movement for the forfeited amount and unattaches the deposit from the
 * no-showed booking, so the existing deposit-on-file minting resurfaces it in
 * the money-cohort queue with no extra wiring.
 */
export function reinstateDeposit(
  depositId: string,
  by: string,
  opts?: { at?: string; note?: string },
): Deposit | undefined {
  const brain = useBrainStore.getState();
  const deposit = brain.deposits[depositId];
  if (!deposit) return undefined;
  const ledger = deposit.ledger ?? [];
  const forfeited = ledger.some((e) => e.kind === 'forfeit') && depositPaidAud(deposit) <= 0;
  // Reinstate only acts on a genuinely lapsed deposit: one that was forfeited, or
  // one still linked to a no-showed booking (lapsed before any auto-forfeit).
  const booking = deposit.bookingId ? brain.bookings[deposit.bookingId] : undefined;
  const onNoShow = booking?.status === 'no-show';
  if (!forfeited && !onNoShow) return deposit;
  const at = opts?.at ?? nowIso();
  // Reverse the forfeit (when there was one) for the forfeited amount; either way
  // unattach from the dead booking so deposit-on-file resurfaces it in the queue.
  const nextLedger = forfeited
    ? [
        ...ledger,
        {
          at,
          amountAud:
            [...ledger].reverse().find((e) => e.kind === 'forfeit')?.amountAud ?? deposit.amountAud,
          kind: 'reinstate' as const,
          by,
          note: opts?.note ?? 'Reinstated by management',
        },
      ]
    : ledger;
  const updated: Deposit = { ...deposit, status: 'paid', bookingId: undefined, ledger: nextLedger };
  brain.upsertDeposit(updated);
  return updated;
}

/**
 * Reinstate a forfeited deposit (B4, 75ng; Anas 1 Oct Q3): Ops or the owner,
 * with a typed reason. Served, it is the ONE server intent — which checks who is
 * signed in, records the reason and who on a `reinstate` movement and the
 * activity feed, and puts the money back on file for 12 months — and the store
 * mirrors the committed state (so the row reads Paid straight away, not
 * FORFEITED until a reload). Store-fallback keeps the old local write.
 */
export function reinstateDepositWithReason(
  depositId: string,
  reason: string,
): Promise<{ ok: true } | { ok: false; message: string }> {
  const trimmed = reason.trim();
  if (!trimmed) return Promise.resolve({ ok: false, message: 'Say why the deposit is being reinstated.' });
  const by = currentActorId() ?? 'system';
  if (isStoreFallbackEnabled()) {
    const out = reinstateDeposit(depositId, by, { note: trimmed });
    return Promise.resolve(out ? { ok: true } : { ok: false, message: 'That deposit is not on file.' });
  }
  return reinstateDepositIntent(depositId, trimmed).then((res) => {
    if (!res.ok) {
      const copy = intentFailureCopy(res);
      return { ok: false as const, message: res.message ?? copy.body };
    }
    // The server's own instant, so the mirrored movement matches the row on file
    // (the ledger merge keys on it — a different `at` would read as a new row).
    const at = res.at || nowIso();
    withServerAuthoredWrites([['deposits', depositId]], () => {
      const b = useBrainStore.getState();
      const dep = b.deposits[depositId];
      if (!dep) return;
      b.upsertDeposit({
        ...dep,
        status: 'paid',
        bookingId: undefined,
        heldBookingId: undefined,
        ...(res.expiresAt ? { expiresAt: res.expiresAt } : {}),
        ledger: [...(dep.ledger ?? []), { at, amountAud: res.amountAud, kind: 'reinstate', by, note: trimmed }],
      });
    });
    return { ok: true as const };
  });
}
